import { useEffect, useMemo, useRef, useState } from 'react';
import { api, pollJob } from './api';
import type {
  AccountResult,
  ApifyRunInfo,
  ApifyRunStatus,
  ArchiveEvent,
  AssetCondition,
  Category,
  CollectionRun,
  GuidelineFolder,
  GuidelineStep,
  GuidelineTool,
  KeywordTrend,
  Me,
  NavigationTab,
  Platform,
  PlatformFilter,
  PostResult,
  RecentPost,
  RecommendationRun,
  Role,
  TrendKeywordType,
  UserRecord,
} from './types';

type LoadState<T> =
  | { status: 'idle' | 'loading'; data: T | null; error: '' }
  | { status: 'ready'; data: T; error: '' }
  | { status: 'error'; data: T | null; error: string };

type ExploreSubtab = 'keyword' | 'hashtag' | 'account' | 'url';
type AssetTarget = 'recommendation' | 'example';
type Notice = { tone: 'ok' | 'error'; text: string } | null;

type JobPollState =
  | { status: 'idle' }
  | { status: 'polling'; run: ApifyRunInfo }
  | { status: 'done'; run: ApifyRunInfo }
  | { status: 'error'; run?: ApifyRunInfo; error: string };

type OpenAccountRef = { platform: Platform; username: string; accountId?: string };

const defaultTabs: NavigationTab[] = [
  { key: 'dashboard', label: '대시보드' },
  { key: 'explore', label: '탐색' },
  { key: 'asset', label: '에셋' },
  { key: 'collect', label: '수집' },
  { key: 'account', label: '계정' },
  { key: 'trend', label: '트렌드' },
  { key: 'recommendation', label: '추천' },
  { key: 'example', label: '예시' },
  { key: 'archive', label: '확정' },
  { key: 'guideline', label: '지침' },
];

const adminRoutes: Record<string, string> = {
  dashboard: '/admin/dashboard',
  explore: '/admin/explore',
  asset: '/admin/assets',
  collect: '/admin/collect',
  account: '/admin/accounts',
  trend: '/admin/trends',
  recommendation: '/admin/recommendations',
  example: '/admin/examples',
  archive: '/admin/archive',
  guideline: '/admin/guidelines',
};

const editorRoutes: Record<string, string> = {
  recommendation: '/editor/recommendations',
  example: '/editor/examples',
  archive: '/editor/archive',
  guideline: '/editor/guidelines',
};

const platformOptions: string[][] = [
  ['all', '전체'],
  ['instagram', 'Instagram'],
  ['tiktok', 'TikTok'],
];

const platformOnlyOptions: string[][] = [
  ['instagram', 'Instagram'],
  ['tiktok', 'TikTok'],
];

const dateRangeOptions: string[][] = [
  ['1d', '1일'],
  ['7d', '7일'],
  ['30d', '30일'],
  ['all', '전체'],
];

const resultSortOptions: string[][] = [
  ['popular', '인기순'],
  ['latest', '최신순'],
];

const accountSortOptions: string[][] = [
  ['followers', '팔로워순'],
  ['avgViews', '평균조회수순'],
  ['engagement', '참여율순'],
  ['recent', '최근수집순'],
];

const trendWindowOptions: string[][] = [
  ['24h', '24시간'],
  ['7d', '7일'],
];

const trendSubtabs: [TrendKeywordType, string][] = [
  ['hashtag', '인기 해시태그'],
  ['caption', '캡션 키워드'],
  ['related', '연관 키워드'],
  ['discover', '연관 해시태그'],
];

function App() {
  const [platform, setPlatform] = useState<PlatformFilter>(() => (localStorage.getItem('pick7reels.platform') as PlatformFilter) || 'all');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [devEmail, setDevEmail] = useState(() => localStorage.getItem('pick7reels.devEmail') || '');
  const [meState, setMeState] = useState<LoadState<Me>>({ status: 'loading', data: null, error: '' });
  const [tabsState, setTabsState] = useState<LoadState<NavigationTab[]>>({ status: 'loading', data: null, error: '' });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [notice, setNotice] = useState<Notice>(null);
  const [editorScope, setEditorScope] = useState('');
  const [editors, setEditors] = useState<UserRecord[]>([]);
  const [apifyKeyCount, setApifyKeyCount] = useState<number | null>(null);
  const [shellRefresh, setShellRefresh] = useState(0);
  const tabs = tabsState.data || defaultTabs;
  const me = meState.data;

  useEffect(() => {
    const mapped = platform === 'instagram' ? 'ig' : platform === 'tiktok' ? 'tt' : 'all';
    document.body.dataset.platform = mapped;
    localStorage.setItem('pick7reels.platform', platform);
  }, [platform]);

  useEffect(() => {
    if (me?.role !== 'admin') {
      setEditors([]);
      setEditorScope('');
      return undefined;
    }
    let active = true;
    api.users()
      .then((data) => {
        if (!active) return;
        setEditors((data.users || []).filter((user) => user.role === 'editor' && Number(user.is_active) === 1));
      })
      .catch(() => {
        if (active) setEditors([]);
      });
    api.keys()
      .then((keys) => { if (active) setApifyKeyCount((keys || []).length); })
      .catch(() => { if (active) setApifyKeyCount(null); });
    return () => { active = false; };
  }, [me?.role, devEmail]);

  useEffect(() => {
    let active = true;
    async function loadShell() {
      setMeState({ status: 'loading', data: null, error: '' });
      setTabsState({ status: 'loading', data: null, error: '' });
      try {
        const [meData, navigation] = await Promise.all([api.me(), api.navigation()]);
        if (!active) return;
        setMeState({ status: 'ready', data: meData, error: '' });
        setTabsState({ status: 'ready', data: navigation.tabs, error: '' });
        const nextTab = resolveTabForPath(window.location.pathname, meData.role, navigation.tabs);
        setActiveTab(nextTab);
        replaceAppPath(pathForTab(meData.role, nextTab));
      } catch (error) {
        if (!active) return;
        const message = error instanceof Error ? error.message : '로그인이 필요합니다';
        setMeState({ status: 'error', data: null, error: message });
        setTabsState({ status: 'error', data: defaultTabs, error: message });
      }
    }
    loadShell();
    return () => { active = false; };
  }, [devEmail, shellRefresh]);

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 2400);
    return () => window.clearTimeout(id);
  }, [notice]);

  useEffect(() => {
    if (!me || !tabs.length) return undefined;
    const handlePopState = () => {
      setActiveTab(resolveTabForPath(window.location.pathname, me.role, tabs));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [me, tabs]);

  async function logout() {
    try {
      await api.logout();
    } catch {
      // 세션이 이미 만료된 경우에도 로컬 상태는 초기화한다.
    }
    setShellRefresh((value) => value + 1);
  }

  return (
    <>
      <header className="hdr">
        <div className="logo" aria-label="Pick7reels">
          <em>📱</em>
          <b>Pick7reels</b>
        </div>
        <div className="fmt-toggle" role="group" aria-label="플랫폼">
          <button className={`fmt-btn ${platform === 'instagram' ? 'active' : ''}`} onClick={() => setPlatform('instagram')}>Instagram</button>
          <button className={`fmt-btn ${platform === 'tiktok' ? 'active' : ''}`} onClick={() => setPlatform('tiktok')}>TikTok</button>
          <button className={`fmt-btn ${platform === 'all' ? 'active' : ''}`} onClick={() => setPlatform('all')}>전체</button>
        </div>
        <div className="hdr-spacer" />
        <div className="hdr-right">
          <div className="key-dots" title="API 상태">
            <span className={`kdot ${meState.status === 'ready' ? 'on' : ''}`} />
            <span className={`kdot ${(apifyKeyCount || 0) > 0 ? 'on' : ''}`} />
            <span className={`kdot ${meState.status === 'error' ? 'err' : ''}`} />
          </div>
          <span className="key-txt">{me?.role === 'admin' && apifyKeyCount != null ? `APIFY·${apifyKeyCount}` : (me?.email || 'not signed')}</span>
          {me?.role === 'admin' && (
            <EditorDropdown
              editors={editors}
              editorScope={editorScope}
              onSelect={setEditorScope}
              onOpenSettings={() => setSettingsOpen(true)}
            />
          )}
          {me?.role === 'admin' && <span className="admin-badge">관리자</span>}
          {me && <button className="btn-secondary btn-sm" onClick={() => void logout()}>로그아웃</button>}
          <button className={`cfg-btn ${settingsOpen ? 'open' : ''}`} title="설정" onClick={() => setSettingsOpen((value) => !value)}>⚙</button>
        </div>
      </header>

      <nav className="tab-bar">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`hdr-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(tab.key);
              window.history.pushState(null, '', pathForTab(me?.role || 'editor', tab.key));
            }}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {settingsOpen && (
        <SettingsPanel
          devEmail={devEmail}
          setDevEmail={setDevEmail}
          role={me?.role || 'editor'}
          notify={setNotice}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      {notice && <div className={`toast ${notice.tone}`}>{notice.text}</div>}

      <main className="app-main">
        {meState.status === 'error' ? (
          <AuthPanel error={meState.error} devEmail={devEmail} onLoggedIn={() => setShellRefresh((value) => value + 1)} notify={setNotice} />
        ) : (
          <DashboardPanel
            activeTab={activeTab}
            setActiveTab={(key) => {
              setActiveTab(key);
              window.history.pushState(null, '', pathForTab(me?.role || 'editor', key));
            }}
            role={me?.role || 'editor'}
            platform={platform}
            editorScope={editorScope}
            notify={setNotice}
          />
        )}
      </main>
    </>
  );
}

// admin-demo v2 헤더의 편집자 접근 관리 드롭다운.
// 단순 미리보기가 아니라 현재 관리할 editor_email scope 선택기다.
const EDITOR_AVATAR_GRADIENTS = [
  'linear-gradient(135deg,#4c8dff,#2dd4a8)',
  'linear-gradient(135deg,#a78bfa,#4c8dff)',
  'linear-gradient(135deg,#f0b429,#ff4466)',
];

function editorGradient(index: number) {
  return EDITOR_AVATAR_GRADIENTS[((index % EDITOR_AVATAR_GRADIENTS.length) + EDITOR_AVATAR_GRADIENTS.length) % EDITOR_AVATAR_GRADIENTS.length];
}

function EditorDropdown({
  editors,
  editorScope,
  onSelect,
  onOpenSettings,
}: {
  editors: UserRecord[];
  editorScope: string;
  onSelect: (email: string) => void;
  onOpenSettings: () => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedIndex = editors.findIndex((editor) => editor.email === editorScope);
  const selected = selectedIndex >= 0 ? editors[selectedIndex] : null;

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('.editor-dd-wrap') || target.closest('.editor-panel')) return;
      setOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  return (
    <div className="editor-dd-wrap">
      <button className={`editor-dd-btn ${open ? 'open' : ''}`} onClick={() => setOpen((value) => !value)}>
        <span
          className="ed-avatar"
          style={{ background: selected ? editorGradient(selectedIndex) : 'var(--bg4)' }}
        >
          {selected ? (selected.display_name || selected.email)[0] : '?'}
        </span>
        <span>{selected ? (selected.display_name || selected.email) : '편집자 선택'}</span>
        <span className="ed-chevron">▼</span>
      </button>
      <div className={`editor-panel ${open ? 'on' : ''}`}>
        <div className="ep-hdr">
          <div className="ep-title">편집자 접근 관리</div>
        </div>
        <div className="ep-list">
          {editors.map((editor, index) => (
            <div
              className={`ep-item ${editor.email === editorScope ? 'selected' : ''}`}
              key={editor.id}
              onClick={() => {
                onSelect(editor.email);
                window.setTimeout(() => setOpen(false), 180);
              }}
            >
              <div className="ep-av" style={{ background: editorGradient(index) }}>
                {(editor.display_name || editor.email)[0]}
              </div>
              <div className="ep-info">
                <div className="ep-name">{editor.display_name || editor.email}</div>
                <div className="ep-email">{editor.email}</div>
              </div>
              <div className="ep-status">
                <span className={`ep-dot ${Number(editor.is_active) === 1 ? 'active' : 'pending'}`} title={Number(editor.is_active) === 1 ? '접속 허용됨' : '초대 대기 중'} />
                {editor.email === editorScope && <span className="ep-check">✓</span>}
              </div>
            </div>
          ))}
          {!editors.length && <div className="empty-meta" style={{ padding: '10px' }}>활성 편집자가 없습니다</div>}
        </div>
        <div className="ep-foot">
          <button className="ep-add-btn" onClick={() => { setOpen(false); onOpenSettings(); }}>+ 편집자 추가</button>
          <button className="ep-manage-btn" onClick={() => { setOpen(false); onOpenSettings(); }}>관리</button>
        </div>
      </div>
    </div>
  );
}

function PlatformBadge({ platform }: { platform: Platform | PlatformFilter | undefined }) {
  if (!platform || platform === 'all') return null;
  return <span className={`pf-badge ${platform === 'instagram' ? 'pf-ig' : 'pf-tt'}`}>{platform === 'instagram' ? 'IG' : 'TT'}</span>;
}

function useJobPolling() {
  const [job, setJob] = useState<JobPollState>({ status: 'idle' });
  const signalRef = useRef<{ cancelled: boolean }>({ cancelled: false });

  useEffect(() => () => { signalRef.current.cancelled = true; }, []);

  function start(run: ApifyRunInfo) {
    signalRef.current = { cancelled: false };
    const signal = signalRef.current;
    setJob({ status: 'polling', run });
    pollJob(run.id, {
      onTick: (nextRun) => { if (!signal.cancelled) setJob({ status: 'polling', run: nextRun }); },
      signal,
    })
      .then((finalRun) => {
        if (signal.cancelled) return;
        if (finalRun.status === 'succeeded') setJob({ status: 'done', run: finalRun });
        else setJob({ status: 'error', run: finalRun, error: finalRun.errorMessage || finalRun.error_message || `작업이 ${finalRun.status} 상태로 종료되었습니다` });
      })
      .catch((error) => {
        if (signal.cancelled) return;
        setJob({ status: 'error', error: error instanceof Error ? error.message : '수집 실패' });
      });
  }

  function reset() {
    signalRef.current.cancelled = true;
    setJob({ status: 'idle' });
  }

  return { job, start, reset };
}

function RunStatusBand({ job, onRetry }: { job: JobPollState; onRetry?: () => void }) {
  if (job.status === 'idle') return null;
  if (job.status === 'polling') {
    const run = job.run;
    const isPending = run.status === 'pending';
    const count = run.itemCount ?? run.item_count ?? 0;
    return (
      <div className="run-status-band">
        <span className={`status-pill ${isPending ? 'pending' : 'running'}`}>{!isPending && <span className="spin" />}{isPending ? '대기중' : '실행중'}</span>
        <span className="run-status-meta">{isPending ? `실행 #${run.id} 대기 중` : `${formatNumber(count)}개 수집 중`}</span>
      </div>
    );
  }
  if (job.status === 'done') {
    const count = job.run.itemCount ?? job.run.item_count ?? 0;
    return (
      <div className="run-status-band">
        <span className="status-pill succeeded">완료</span>
        <span className="run-status-meta">{formatNumber(count)}개 수집됨</span>
      </div>
    );
  }
  return (
    <div className="run-status-band">
      <span className="status-pill failed">실패</span>
      <span className="run-status-meta">{job.error}</span>
      {onRetry && <button className="btn-secondary btn-sm" onClick={onRetry}>재시도</button>}
    </div>
  );
}

function RunStatusPill({ status }: { status: ApifyRunStatus }) {
  const labels: Record<ApifyRunStatus, string> = { pending: '대기중', running: '실행중', succeeded: '완료', failed: '실패', timeout: '시간초과', aborted: '중단' };
  const cls = status === 'succeeded' ? 'succeeded' : status === 'running' ? 'running' : status === 'pending' ? 'pending' : 'failed';
  return <span className={`status-pill ${cls}`}>{status === 'running' && <span className="spin" />}{labels[status] || status}</span>;
}

function AccountPostsPanel({ account, onBack, notify }: { account: OpenAccountRef; onBack: () => void; notify: (notice: Notice) => void }) {
  const [view, setView] = useState<'card' | 'table'>('table');
  const state = useApiData(
    `account-posts-${account.platform}-${account.username}-${account.accountId || ''}`,
    () => api.socialPosts({ platform: account.platform, username: account.username, accountId: account.accountId, limit: 50, sort: 'latest' }),
  );
  const posts = state.data?.posts || [];
  const followerCount = posts[0] ? (posts[0].followerCount ?? posts[0].follower_count) : null;

  return (
    <section className="panel active">
      <div className="content">
        <div className="similar-head">
          <button className="btn-secondary" onClick={onBack}>← 뒤로가기</button>
          <div className="similar-title">
            <strong><PlatformBadge platform={account.platform} /> @{account.username}</strong>
            <span>팔로워 {formatNumber(followerCount)}</span>
          </div>
          <div className="toolbar-right"><ViewToggle value={view} onChange={setView} /></div>
        </div>
        {state.status === 'loading' && <EmptyPanel title="게시물을 불러오는 중" />}
        {state.status === 'error' && <ErrorBox text={state.error} />}
        {state.status === 'ready' && (
          <PostResults
            items={posts}
            view={view}
            mode="explore"
            onArchive={async (item) => {
              await api.addArchive(postArchivePayload(item));
              notify({ tone: 'ok', text: '확정에 등록했습니다' });
            }}
          />
        )}
      </div>
    </section>
  );
}

function DashboardPanel({
  activeTab,
  setActiveTab,
  role,
  platform,
  editorScope,
  notify,
}: {
  activeTab: string;
  setActiveTab: (key: string) => void;
  role: Role;
  platform: PlatformFilter;
  editorScope: string;
  notify: (notice: Notice) => void;
}) {
  if (activeTab === 'dashboard') return <DashboardView platform={platform} setActiveTab={setActiveTab} notify={notify} />;
  if (activeTab === 'explore') return <ExploreView platform={platform} editorScope={editorScope} notify={notify} />;
  if (activeTab === 'asset') return <AssetsView editorScope={editorScope} notify={notify} />;
  if (activeTab === 'collect') return <CollectionView platform={platform} editorScope={editorScope} notify={notify} />;
  if (activeTab === 'account') return <AccountsView platform={platform} notify={notify} />;
  if (activeTab === 'trend') return <TrendsView platform={platform} setActiveTab={setActiveTab} notify={notify} />;
  if (activeTab === 'recommendation') return <RecommendationsView role={role} platform={platform} editorScope={editorScope} notify={notify} />;
  if (activeTab === 'example') return <ExamplesView notify={notify} />;
  if (activeTab === 'archive') return <ArchiveView notify={notify} />;
  if (activeTab === 'guideline') return <GuidelinesView role={role} notify={notify} />;
  return <EmptyPanel title="준비 중" />;
}

function DashboardView({
  platform,
  setActiveTab,
  notify,
}: {
  platform: PlatformFilter;
  setActiveTab: (key: string) => void;
  notify: (notice: Notice) => void;
}) {
  const [localPlatform, setLocalPlatform] = useState<PlatformFilter>(platform);
  const [openAccount, setOpenAccount] = useState<OpenAccountRef | null>(null);

  useEffect(() => { setLocalPlatform(platform); }, [platform]);

  if (openAccount) {
    return <AccountPostsPanel account={openAccount} onBack={() => setOpenAccount(null)} notify={notify} />;
  }

  function goExplore() {
    setActiveTab('explore');
  }

  function goExploreWithKeyword(keyword: string) {
    try { sessionStorage.setItem('pick7reels.exploreKeyword', keyword); } catch { /* storage 차단 환경은 무시 */ }
    setActiveTab('explore');
  }

  return (
    <section className="panel active">
      <div className="sub-tabs">
        {(['all', 'instagram', 'tiktok'] as PlatformFilter[]).map((key) => (
          <button key={key} className={`sub-tab ${localPlatform === key ? 'active' : ''}`} onClick={() => setLocalPlatform(key)}>
            {key === 'all' ? '통합' : key === 'instagram' ? 'Instagram' : 'TikTok'}
          </button>
        ))}
      </div>
      <div className="dash-grid">
        <TopPostsSection platform={localPlatform} onOpenAccount={setOpenAccount} onGoExplore={goExplore} />
        <TopKeywordsSection platform={localPlatform} onKeywordClick={goExploreWithKeyword} onGoExplore={goExplore} />
        <TopAccountsSection platform={localPlatform} onOpenAccount={setOpenAccount} onGoExplore={goExplore} />
      </div>
    </section>
  );
}

function DashEmpty({ onGoExplore }: { onGoExplore: () => void }) {
  return (
    <div className="empty-box">
      <div className="empty-title">아직 집계된 데이터가 없습니다 — 탐색에서 수집을 실행해주세요</div>
      <button className="btn-secondary btn-sm" style={{ marginTop: '10px' }} onClick={onGoExplore}>탐색으로 이동</button>
    </div>
  );
}

function TopPostsSection({
  platform,
  onOpenAccount,
  onGoExplore,
}: {
  platform: PlatformFilter;
  onOpenAccount: (account: OpenAccountRef) => void;
  onGoExplore: () => void;
}) {
  const [refresh, setRefresh] = useState(0);
  const state = useApiData(`dash-posts-${platform}-${refresh}`, () => api.socialTrends({ entity: 'post', platform, window: '24h', limit: 10 }));
  const items = (state.data?.items || []) as PostResult[];
  return (
    <div className="dash-sec">
      <div className="dash-sec-hdr">
        <div className="dash-sec-title">🔥 오늘의 인기 게시물</div>
        <button className="dash-refresh-btn" onClick={() => setRefresh((value) => value + 1)}>↻</button>
      </div>
      <div className="dash-sec-body">
        {state.status === 'loading' && <EmptyPanel title="불러오는 중" />}
        {state.status === 'error' && <ErrorBox text={state.error} />}
        {state.status === 'ready' && !items.length && <DashEmpty onGoExplore={onGoExplore} />}
        {items.length > 0 && (
          <div className="rec-vid-list">
            {items.map((item, index) => (
              <div className="rec-vid-item" key={postId(item) || index}>
                <div className={`rec-rank-num ${index === 0 ? 'n1' : index === 1 ? 'n2' : index === 2 ? 'n3' : ''}`}>{index + 1}</div>
                <a className="rec-thumb" href={postUrl(item)} target="_blank" rel="noreferrer">
                  {thumb(item) ? <img src={thumb(item)} alt="" /> : <span className="thumb-fallback">🎬</span>}
                </a>
                <div className="rec-vid-info">
                  <span className="rec-vid-title">{captionPreview(item, 60)}</span>
                  <button
                    className="rec-vid-ch"
                    style={{ background: 'none', border: 0, padding: 0, cursor: item.username ? 'pointer' : 'default', textAlign: 'left' }}
                    onClick={() => item.username && onOpenAccount({ platform: item.platform, username: item.username, accountId: item.accountId || item.account_id })}
                  >
                    <PlatformBadge platform={item.platform} /> {accountTitle(item)}
                  </button>
                  <div className="rec-vid-stats">
                    <span className="c-blue">{formatNumber(viewCount(item))} 조회</span>
                    <span>{formatViralScore(item)}점</span>
                    <span>{hoursAgo(item.uploadedAt || item.uploaded_at || item.published_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TopKeywordsSection({
  platform,
  onKeywordClick,
  onGoExplore,
}: {
  platform: PlatformFilter;
  onKeywordClick: (keyword: string) => void;
  onGoExplore: () => void;
}) {
  const [refresh, setRefresh] = useState(0);
  const state = useApiData(`dash-keywords-${platform}-${refresh}`, () => api.socialTrends({ entity: 'keyword', platform, window: '24h', limit: 15 }));
  const items = (state.data?.items || []) as KeywordTrend[];
  return (
    <div className="dash-sec">
      <div className="dash-sec-hdr">
        <div className="dash-sec-title">🔎 인기 키워드</div>
        <button className="dash-refresh-btn" onClick={() => setRefresh((value) => value + 1)}>↻</button>
      </div>
      <div className="dash-sec-body">
        {state.status === 'loading' && <EmptyPanel title="불러오는 중" />}
        {state.status === 'error' && <ErrorBox text={state.error} />}
        {state.status === 'ready' && !items.length && <DashEmpty onGoExplore={onGoExplore} />}
        {items.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {items.map((kw) => {
              const growth = kw.growth24h ?? kw.growth_24h ?? 0;
              return (
                <button key={`${kw.keyword}-${kw.type}`} className="trend-chip" onClick={() => onKeywordClick(kw.keyword)}>
                  {kw.keyword}
                  {(kw.crossPlatform || kw.cross_platform) ? <span className="xplat-chip">교차</span> : null}
                  <span className={growth >= 0 ? 'trend-up' : 'trend-down'}>{growth >= 0 ? '▲' : '▼'}{Math.abs(Math.round(growth))}%</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function TopAccountsSection({
  platform,
  onOpenAccount,
  onGoExplore,
}: {
  platform: PlatformFilter;
  onOpenAccount: (account: OpenAccountRef) => void;
  onGoExplore: () => void;
}) {
  const [refresh, setRefresh] = useState(0);
  const state = useApiData(`dash-accounts-${platform}-${refresh}`, () => api.socialTrends({ entity: 'account', platform, window: '24h', limit: 5 }));
  const items = (state.data?.items || []) as AccountResult[];
  return (
    <div className="dash-sec wide">
      <div className="dash-sec-hdr">
        <div className="dash-sec-title">🚀 급상승 계정</div>
        <button className="dash-refresh-btn" onClick={() => setRefresh((value) => value + 1)}>↻</button>
      </div>
      <div className="dash-sec-body">
        {state.status === 'loading' && <EmptyPanel title="불러오는 중" />}
        {state.status === 'error' && <ErrorBox text={state.error} />}
        {state.status === 'ready' && !items.length && <DashEmpty onGoExplore={onGoExplore} />}
        {items.length > 0 && (
          <div className="channel-card-list">
            {items.map((account) => (
              <div className="channel-card" key={account.accountId || account.account_id}>
                <div className="rank-badge">#{account.rank || '-'}</div>
                <span className="channel-avatar sm">
                  {account.avatar ? <img src={account.avatar} alt="" /> : <span>👤</span>}
                </span>
                <div className="channel-main">
                  <div className="channel-title"><PlatformBadge platform={account.platform} /> {accountTitle(account)}</div>
                  <div className="channel-meta">
                    팔로워 {formatNumber(account.followerCount ?? account.follower_count)} · 평균조회수 {formatNumber(account.avgViewsRecent ?? account.avg_views_recent)} · 성장 {formatMetric(account.growth24h ?? account.growth_24h)}%
                  </div>
                </div>
                <div className="row-actions">
                  <button className="mini-btn" onClick={() => onOpenAccount({ platform: account.platform, username: account.username, accountId: account.accountId || account.account_id })}>계정 게시물</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const exploreSubtabMeta: Record<ExploreSubtab, { icon: string; label: string; placeholder: string }> = {
  keyword: { icon: '🔎', label: '키워드 탐색', placeholder: '검색 키워드' },
  hashtag: { icon: '#️⃣', label: '해시태그 탐색', placeholder: '해시태그 (# 없이)' },
  account: { icon: '👤', label: '계정 탐색', placeholder: '계정 아이디 (@ 없이)' },
  url: { icon: '🔗', label: 'URL 수집', placeholder: 'Instagram 릴스 / TikTok 링크' },
};

function ExploreView({
  platform,
  editorScope,
  notify,
}: {
  platform: PlatformFilter;
  editorScope: string;
  notify: (notice: Notice) => void;
}) {
  const [subtab, setSubtab] = useState<ExploreSubtab>('keyword');
  const [platformSel, setPlatformSel] = useState<PlatformFilter>(platform);
  const [queryInput, setQueryInput] = useState('');
  const [limit, setLimit] = useState(10);
  const [dateRange, setDateRange] = useState('7d');
  const [sort, setSort] = useState('popular');
  const [view, setView] = useState<'card' | 'table'>('table');
  const [results, setResults] = useState<PostResult[]>([]);
  const [openAccount, setOpenAccount] = useState<OpenAccountRef | null>(null);
  const jobPolling = useJobPolling();

  useEffect(() => {
    if (subtab !== 'keyword' && platformSel === 'all') setPlatformSel('instagram');
  }, [subtab, platformSel]);

  useEffect(() => {
    try {
      const prefill = sessionStorage.getItem('pick7reels.exploreKeyword');
      if (prefill) {
        sessionStorage.removeItem('pick7reels.exploreKeyword');
        setSubtab('keyword');
        setQueryInput(prefill);
      }
    } catch {
      // 세션 스토리지 접근 불가 환경은 무시한다.
    }
  }, []);

  useEffect(() => {
    if (jobPolling.job.status !== 'done') return;
    const run = jobPolling.job.run;
    const runPlatform: PlatformFilter | undefined = run.platform && run.platform !== 'all' ? run.platform : (platformSel === 'all' ? undefined : platformSel);
    const params: { platform?: PlatformFilter; limit: number; sort: string; keyword?: string; username?: string } = {
      platform: runPlatform,
      limit,
      sort,
    };
    if (subtab === 'keyword' || subtab === 'hashtag') params.keyword = queryInput.trim();
    if (subtab === 'account') params.username = queryInput.trim().replace(/^@/, '');
    api.socialPosts(params)
      .then((data) => {
        setResults(data.posts || []);
        notify({ tone: 'ok', text: `${(data.posts || []).length}개 게시물을 수집했습니다` });
      })
      .catch((error) => notify({ tone: 'error', text: error instanceof Error ? error.message : '결과 조회 실패' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPolling.job.status === 'done' ? jobPolling.job.run.id : null]);

  async function runSearch() {
    const trimmedInput = queryInput.trim();
    if (subtab !== 'url' && !trimmedInput) {
      notify({ tone: 'error', text: '검색어를 입력해주세요' });
      return;
    }
    if (subtab === 'url' && !trimmedInput) {
      notify({ tone: 'error', text: '수집할 URL을 입력해주세요' });
      return;
    }
    if (subtab !== 'keyword' && platformSel === 'all') {
      notify({ tone: 'error', text: '플랫폼을 Instagram 또는 TikTok 중 하나로 선택해주세요' });
      return;
    }
    jobPolling.reset();
    setResults([]);
    try {
      if (subtab === 'keyword') {
        const discoverPlatform = platformSel === 'all' ? 'both' : platformSel;
        const response = await api.socialDiscover({ platform: discoverPlatform, query: trimmedInput, limit, dateRange, sort });
        if (response.items) {
          setResults(response.items);
          notify({ tone: 'ok', text: `${response.items.length}개 게시물을 수집했습니다` });
        } else if (response.run) {
          notify({ tone: 'ok', text: `수집을 시작했습니다 · #${response.run.id}` });
          jobPolling.start(response.run);
        }
      } else if (subtab === 'hashtag') {
        const { run } = await api.collectHashtag({ platform: platformSel as Platform, hashtag: trimmedInput.replace(/^#/, ''), limit });
        notify({ tone: 'ok', text: `수집을 시작했습니다 · #${run.id}` });
        jobPolling.start(run);
      } else if (subtab === 'account') {
        const { run } = await api.collectAccount({ platform: platformSel as Platform, username: trimmedInput.replace(/^@/, ''), limit });
        notify({ tone: 'ok', text: `수집을 시작했습니다 · #${run.id}` });
        jobPolling.start(run);
      } else {
        const { post } = await api.collectUrl({ platform: platformSel as Platform, url: trimmedInput });
        setResults([post]);
        notify({ tone: 'ok', text: '1개 게시물을 수집했습니다' });
      }
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '수집 실패' });
    }
  }

  async function registerCondition(target: AssetTarget) {
    if (!editorScope) {
      notify({ tone: 'error', text: '편집자를 먼저 선택해주세요' });
      return;
    }
    const trimmedInput = queryInput.trim();
    const conditionPlatform: Platform = platformSel === 'all' ? 'instagram' : platformSel;
    const conditionLabel = `${exploreSubtabMeta[subtab].label} · ${trimmedInput || '-'} · ${platformSel === 'all' ? '전체' : platformSel} · ${limit}개`;
    try {
      await api.createAsset({
        target,
        editorEmail: editorScope,
        conditionType: subtab,
        label: conditionLabel,
        normalizedKey: trimmedInput,
        source: `social_${subtab}`,
        country: '',
        categoryKey: 'all',
        platform: conditionPlatform,
        limitCount: limit,
        payload: { subtab, query: trimmedInput, dateRange, sort, platform: platformSel },
      });
      notify({ tone: 'ok', text: `${target === 'recommendation' ? '추천' : '예시'} 에셋에 조건을 등록했습니다` });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '에셋 등록 실패' });
    }
  }

  async function archivePost(item: PostResult) {
    try {
      await api.addArchive(postArchivePayload(item));
      notify({ tone: 'ok', text: '확정에 등록했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '확정 등록 실패' });
    }
  }

  function openAccountPosts(item: PostResult) {
    if (!item.username) {
      notify({ tone: 'error', text: '계정 정보가 없습니다' });
      return;
    }
    setOpenAccount({ platform: item.platform, username: item.username, accountId: item.accountId || item.account_id });
  }

  if (openAccount) {
    return <AccountPostsPanel account={openAccount} onBack={() => setOpenAccount(null)} notify={notify} />;
  }

  const isBusy = jobPolling.job.status === 'polling';
  const conditionLabelText = `${exploreSubtabMeta[subtab].label} · ${queryInput.trim() || '-'} · ${platformSel === 'all' ? '전체' : platformSel} · ${limit}개`;

  return (
    <section className="panel active">
      <div className="sub-tabs">
        {(Object.keys(exploreSubtabMeta) as ExploreSubtab[]).map((key) => (
          <button
            key={key}
            className={`sub-tab ${subtab === key ? 'active' : ''}`}
            onClick={() => {
              setSubtab(key);
              setQueryInput('');
              setResults([]);
              jobPolling.reset();
            }}
          >
            {exploreSubtabMeta[key].icon} {exploreSubtabMeta[key].label}
          </button>
        ))}
        <div className="sub-r">
          <span className="sec-meta">{results.length ? `${results.length}개 결과` : '탐색 전'}</span>
        </div>
      </div>
      <div className="content">
        <div className="rank-search-bar">
          <span className="rank-filter-lbl">{exploreSubtabMeta[subtab].icon} 입력</span>
          <input
            className="rank-search"
            value={queryInput}
            onChange={(event) => setQueryInput(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') void runSearch(); }}
            placeholder={exploreSubtabMeta[subtab].placeholder}
          />
          <button className="rank-search-btn" type="button" onClick={() => void runSearch()} disabled={isBusy}>
            {isBusy ? '수집 요청 중…' : subtab === 'url' ? '수집' : '탐색'}
          </button>
        </div>
        <div className="rank-filters">
          <span className="rank-filter-lbl">플랫폼</span>
          <Select value={platformSel} onChange={(value) => setPlatformSel(value as PlatformFilter)} options={subtab === 'keyword' ? platformOptions : platformOnlyOptions} />
          <span className="rank-filter-lbl">결과</span>
          <NumberInput value={limit} onChange={setLimit} min={1} max={50} />
          {subtab === 'keyword' && (
            <>
              <span className="rank-filter-lbl">날짜 범위</span>
              <Select value={dateRange} onChange={setDateRange} options={dateRangeOptions} />
            </>
          )}
          {subtab !== 'url' && (
            <>
              <span className="rank-filter-lbl">정렬</span>
              <Select value={sort} onChange={setSort} options={resultSortOptions} />
            </>
          )}
          <div className="toolbar-right"><ViewToggle value={view} onChange={setView} /></div>
        </div>

        <RunStatusBand job={jobPolling.job} onRetry={() => void runSearch()} />

        {results.length > 0 && (
          <div className="condition-register-row">
            <span className="condition-help">{conditionLabelText} · 이 조건을</span>
            <button className="btn-secondary btn-sm green" onClick={() => void registerCondition('recommendation')}>추천 에셋 등록</button>
            <button className="btn-secondary btn-sm purple" onClick={() => void registerCondition('example')}>예시 에셋 등록</button>
          </div>
        )}

        {!results.length && jobPolling.job.status === 'idle' && <EmptyPanel title="탐색 전입니다" />}
        {results.length > 0 && (
          <PostResults
            items={results}
            view={view}
            mode="explore"
            onArchive={archivePost}
            onOpenAccount={openAccountPosts}
          />
        )}
      </div>
    </section>
  );
}

function AssetsView({ editorScope, notify }: { editorScope: string; notify: (notice: Notice) => void }) {
  const [target, setTarget] = useState<AssetTarget>('recommendation');
  const [refresh, setRefresh] = useState(0);
  const assets = useApiData(`assets-${target}-${editorScope}-${refresh}`, () => api.assets({ target, editorEmail: editorScope }));
  const conditions = assets.data?.conditions || [];
  const groups = groupConditions(conditions);

  async function togglePinned(condition: AssetCondition) {
    try {
      await api.updateAsset(condition.id, { isPinned: !condition.is_pinned });
      setRefresh((value) => value + 1);
      notify({ tone: 'ok', text: condition.is_pinned ? '고정을 해제했습니다' : '고정했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '고정 변경 실패' });
    }
  }

  async function remove(condition: AssetCondition) {
    try {
      await api.deleteAsset(condition.id);
      setRefresh((value) => value + 1);
      notify({ tone: 'ok', text: '에셋 조건을 비활성화했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '삭제 실패' });
    }
  }

  return (
    <section className="panel active">
      <SubTabs tabs={[['recommendation', '추천 에셋'], ['example', '예시 에셋']]} active={target} onChange={(key) => setTarget(key as AssetTarget)} />
      <div className="asset-wrap">
        {assets.status === 'loading' && <EmptyPanel title="에셋을 불러오는 중" />}
        {assets.status === 'error' && <ErrorBox text={assets.error} />}
        {assets.status === 'ready' && !conditions.length && <EmptyPanel title="등록된 에셋이 없습니다" />}
        {assets.status === 'ready' && conditions.length > 0 && Object.entries(groups).map(([type, rows]) => (
          <div className="asset-sec" key={type}>
            <div className="asset-sec-hdr">
              <div className="asset-sec-title">{assetTypeIcon(type)} {assetTypeLabel(type)} 등록됨</div>
              <div className="asset-cnt"><span className="cnt-num">{rows.length}</span>개</div>
            </div>
            <div className="asset-sec-body">
              <div className="condition-list">
                {rows.map((condition) => (
                  <ConditionRow
                    key={condition.id}
                    condition={condition}
                    onPin={() => togglePinned(condition)}
                    onDelete={() => remove(condition)}
                  />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

type SimpleStatus =
  | { phase: 'idle' }
  | { phase: 'polling'; label: string; count?: number }
  | { phase: 'done'; count: number }
  | { phase: 'error'; message: string; onRetry?: () => void };

function SimpleStatusBand({ status }: { status: SimpleStatus }) {
  if (status.phase === 'idle') return null;
  if (status.phase === 'polling') {
    return (
      <div className="run-status-band">
        <span className="status-pill running"><span className="spin" />{status.label}</span>
        {status.count != null && <span className="run-status-meta">{formatNumber(status.count)}개 수집 중</span>}
      </div>
    );
  }
  if (status.phase === 'done') {
    return (
      <div className="run-status-band">
        <span className="status-pill succeeded">완료</span>
        <span className="run-status-meta">{formatNumber(status.count)}개 수집됨</span>
      </div>
    );
  }
  return (
    <div className="run-status-band">
      <span className="status-pill failed">실패</span>
      <span className="run-status-meta">{status.message}</span>
      {status.onRetry && <button className="btn-secondary btn-sm" onClick={status.onRetry}>재시도</button>}
    </div>
  );
}

async function pollCollectionRun(id: number, onTick: (run: CollectionRun) => void) {
  const start = Date.now();
  for (;;) {
    const { run } = await api.collectionRun(id);
    onTick(run);
    if (run.status !== 'running' && run.status !== 'pending') return run;
    if (Date.now() - start > 300000) throw new Error('수집 시간 초과 — 실행 기록에서 상태를 확인해주세요');
    await new Promise((resolve) => setTimeout(resolve, 2500));
  }
}

function CollectionView({
  platform,
  editorScope,
  notify,
}: {
  platform: PlatformFilter;
  editorScope: string;
  notify: (notice: Notice) => void;
}) {
  const [subtab, setSubtab] = useState<'recommendation' | 'example' | 'posts' | 'jobs'>('recommendation');
  const [platformSel, setPlatformSel] = useState<PlatformFilter>(platform);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [refresh, setRefresh] = useState(0);
  const [view, setView] = useState<'card' | 'table'>('table');
  const [collectStatus, setCollectStatus] = useState<SimpleStatus>({ phase: 'idle' });
  const [posts, setPosts] = useState<PostResult[]>([]);
  const target = subtab === 'recommendation' || subtab === 'example' ? subtab : null;
  const assets = useApiData(
    `collect-assets-${target}-${editorScope}-${refresh}`,
    () => (target ? api.assets({ target, editorEmail: editorScope }) : Promise.resolve({ conditions: [] })),
  );
  const conditions = assets.data?.conditions || [];

  useEffect(() => {
    setSelected(new Set(conditions.map((condition) => condition.id)));
  }, [conditions]);

  useEffect(() => {
    setCollectStatus({ phase: 'idle' });
    setPosts([]);
  }, [target, platformSel, editorScope]);

  async function runCollection() {
    if (!target) return;
    if (!editorScope) {
      notify({ tone: 'error', text: '편집자를 먼저 선택해주세요' });
      return;
    }
    setPosts([]);
    setCollectStatus({ phase: 'polling', label: '수집 요청 중' });
    try {
      const { run } = await api.createCollectionRun({ target, editorEmail: editorScope, platform: platformSel, categoryKey: 'all' });
      notify({ tone: 'ok', text: `수집을 시작했습니다 · #${run.id}` });
      setCollectStatus({ phase: 'polling', label: '실행중', count: run.collected_count });
      const finalRun = await pollCollectionRun(run.id, (tick) => setCollectStatus({ phase: 'polling', label: '실행중', count: tick.collected_count }));
      if (/fail|error/i.test(finalRun.status)) {
        setCollectStatus({ phase: 'error', message: `수집이 실패했습니다 (상태: ${finalRun.status})`, onRetry: () => void runCollection() });
        notify({ tone: 'error', text: '수집이 실패했습니다' });
        return;
      }
      setCollectStatus({ phase: 'done', count: finalRun.collected_count });
      const data = await api.collectionPosts({ target, editorEmail: editorScope, platform: platformSel === 'all' ? '' : platformSel });
      setPosts(data.posts || []);
      setRefresh((value) => value + 1);
      notify({ tone: 'ok', text: `${(data.posts || []).length}개 게시물을 수집했습니다` });
    } catch (error) {
      const message = error instanceof Error ? error.message : '수집 실패';
      setCollectStatus({ phase: 'error', message, onRetry: () => void runCollection() });
      notify({ tone: 'error', text: message });
    }
  }

  async function archivePost(item: PostResult) {
    await api.addArchive(postArchivePayload(item));
    notify({ tone: 'ok', text: '확정에 등록했습니다' });
  }

  return (
    <section className="panel active">
      <SubTabs
        tabs={[['recommendation', '추천 수집'], ['example', '예시 수집'], ['posts', '게시물'], ['jobs', '실행 기록']]}
        active={subtab}
        onChange={(key) => setSubtab(key as typeof subtab)}
      />
      {target ? (
        <div className="collect-layout">
          <aside className="asset-picker">
            <div className="asset-picker-title">등록된 에셋</div>
            {conditions.map((condition) => (
              <label className="asset-check-row" key={condition.id}>
                <input
                  type="checkbox"
                  checked={selected.has(condition.id)}
                  onChange={(event) => {
                    setSelected((prev) => {
                      const next = new Set(prev);
                      if (event.target.checked) next.add(condition.id);
                      else next.delete(condition.id);
                      return next;
                    });
                  }}
                />
                <span>{assetTypeIcon(condition.condition_type)} {condition.label}</span>
                <em>{condition.limit_count}</em>
              </label>
            ))}
            {!conditions.length && <div className="empty-meta">등록된 조건이 없습니다</div>}
          </aside>
          <div className="collect-main">
            <div className="filter-band compact">
              <Select value={platformSel} onChange={(value) => setPlatformSel(value as PlatformFilter)} options={platformOptions} />
              <button className="expl-collect-btn" onClick={() => void runCollection()} disabled={collectStatus.phase === 'polling'}>
                {collectStatus.phase === 'polling' ? '수집 요청 중…' : '수집'}
              </button>
              <div className="toolbar-right"><ViewToggle value={view} onChange={setView} /></div>
            </div>
            <SimpleStatusBand status={collectStatus} />
            <PostResults
              items={posts}
              view={view}
              mode="collection"
              emptyTitle={collectStatus.phase === 'polling' ? '수집 중' : collectStatus.phase === 'idle' ? '수집 전입니다' : '수집된 게시물이 없습니다'}
              onArchive={archivePost}
            />
          </div>
        </div>
      ) : subtab === 'posts' ? (
        <PostsBrowser platform={platform} notify={notify} />
      ) : (
        <JobRunsView notify={notify} />
      )}
    </section>
  );
}

function PostsBrowser({ platform, notify }: { platform: PlatformFilter; notify: (notice: Notice) => void }) {
  const [platformSel, setPlatformSel] = useState<PlatformFilter>(platform);
  const [sourceFilter, setSourceFilter] = useState('');
  const [sort, setSort] = useState('latest');
  const [query, setQuery] = useState('');
  const [view, setView] = useState<'card' | 'table'>('table');
  const [posts, setPosts] = useState<PostResult[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 30;

  async function load(nextOffset: number, append: boolean) {
    setLoading(true);
    try {
      const data = await api.socialPosts({
        platform: platformSel === 'all' ? undefined : platformSel,
        q: query.trim() || undefined,
        sort,
        offset: nextOffset,
        limit,
      });
      setPosts((prev) => (append ? [...prev, ...(data.posts || [])] : (data.posts || [])));
      setTotal(data.total || 0);
      setOffset(nextOffset);
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '게시물 조회 실패' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformSel, sort]);

  const filteredPosts = sourceFilter ? posts.filter((post) => (post.source || post.source_key || '').includes(sourceFilter)) : posts;

  return (
    <div className="content">
      <div className="filter-band compact">
        <Select value={platformSel} onChange={(value) => setPlatformSel(value as PlatformFilter)} options={platformOptions} />
        <Select
          value={sourceFilter}
          onChange={setSourceFilter}
          options={[['', '전체 출처'], ['discover', '탐색·키워드'], ['hashtag', '해시태그'], ['account', '계정'], ['url', 'URL 수집'], ['recommendation', '추천 수집'], ['example', '예시 수집']]}
        />
        <input
          className="rank-search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => { if (event.key === 'Enter') void load(0, false); }}
          placeholder="캡션/계정 검색"
        />
        <Select value={sort} onChange={setSort} options={resultSortOptions} />
        <button className="btn-secondary btn-sm" onClick={() => void load(0, false)}>검색</button>
        <div className="toolbar-right"><ViewToggle value={view} onChange={setView} /></div>
      </div>
      {loading && !posts.length ? <EmptyPanel title="불러오는 중" /> : (
        <PostResults items={filteredPosts} view={view} mode="collection" />
      )}
      {posts.length > 0 && posts.length < total && (
        <div className="rank-limit-controls">
          <span className="rank-limit-note">{posts.length} / {total}개 표시 중</span>
          <button className="rank-more-btn" disabled={loading} onClick={() => void load(offset + limit, true)}>{loading ? '불러오는 중…' : '더 보기'}</button>
        </div>
      )}
    </div>
  );
}

function JobRunsView({ notify }: { notify: (notice: Notice) => void }) {
  const [statusFilter, setStatusFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>('all');
  const [refresh, setRefresh] = useState(0);
  const state = useApiData(
    `jobs-${statusFilter}-${platformFilter}-${refresh}`,
    () => api.socialJobs({ status: statusFilter || undefined, platform: platformFilter === 'all' ? undefined : platformFilter, limit: 50 }),
  );
  const runs = state.data?.runs || [];
  const hasActive = runs.some((run) => run.status === 'pending' || run.status === 'running');

  useEffect(() => {
    if (!hasActive) return undefined;
    const id = window.setInterval(() => setRefresh((value) => value + 1), 5000);
    return () => window.clearInterval(id);
  }, [hasActive]);

  async function retry(run: ApifyRunInfo) {
    let inputData: Record<string, unknown> = {};
    try { inputData = JSON.parse(run.inputJson || run.input_json || '{}'); } catch { inputData = {}; }
    const jobType = run.runType || run.job_type || '';
    const runPlatform = (run.platform && run.platform !== 'all' ? run.platform : 'instagram') as Platform;
    try {
      if (jobType.includes('hashtag')) {
        await api.collectHashtag({ platform: runPlatform, hashtag: String(inputData.hashtag || ''), limit: Number(inputData.limit) || 20 });
      } else if (jobType.includes('account')) {
        await api.collectAccount({ platform: runPlatform, username: String(inputData.username || ''), limit: Number(inputData.limit) || 20 });
      } else {
        await api.socialDiscover({ platform: run.platform || 'both', query: String(inputData.query || ''), limit: Number(inputData.limit) || 20 });
      }
      notify({ tone: 'ok', text: '재실행을 요청했습니다' });
      setRefresh((value) => value + 1);
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '재실행 실패' });
    }
  }

  return (
    <div className="content">
      <div className="filter-band compact">
        <Select value={platformFilter} onChange={(value) => setPlatformFilter(value as PlatformFilter)} options={platformOptions} />
        <Select value={statusFilter} onChange={setStatusFilter} options={[['', '전체 상태'], ['pending', '대기중'], ['running', '실행중'], ['succeeded', '완료'], ['failed', '실패']]} />
        <button className="btn-secondary btn-sm" onClick={() => setRefresh((value) => value + 1)}>↻ 새로고침</button>
      </div>
      {state.status === 'loading' && <EmptyPanel title="실행 기록을 불러오는 중" />}
      {state.status === 'error' && <ErrorBox text={state.error} />}
      {state.status === 'ready' && !runs.length && <EmptyPanel title="실행 기록이 없습니다" />}
      {runs.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th><th>유형</th><th>플랫폼</th><th>Actor</th><th>입력 요약</th><th>상태</th><th>수집개수</th><th>오류</th><th>시작</th><th>종료</th><th>작업</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.id}>
                  <td>#{run.id}</td>
                  <td>{run.runType || run.job_type || '-'}</td>
                  <td>{run.platform && run.platform !== 'all' ? <PlatformBadge platform={run.platform} /> : '전체'}</td>
                  <td>{run.actorId || run.actor_id || '-'}</td>
                  <td className="row-title">{summarizeJobInput(run)}</td>
                  <td><RunStatusPill status={run.status} /></td>
                  <td className="num cyan">{formatNumber(run.itemCount ?? run.item_count)}</td>
                  <td>{run.errorMessage || run.error_message || '-'}</td>
                  <td>{formatUnixTime(run.startedAt ?? run.started_at)}</td>
                  <td>{formatUnixTime(run.finishedAt ?? run.finished_at) || '-'}</td>
                  <td><button className="mini-btn" onClick={() => void retry(run)}>재실행</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// 릴스/틱톡 추천 v1.0 뱃지/원값 표시: 창 뱃지, 결측/자료부족 뱃지, 최종 점수
function RecBadges({ item }: { item: PostResult }) {
  if (!item.tab) return null;
  let missing: string[] = [];
  try { missing = JSON.parse(item.missing_badges_json || '[]'); } catch { missing = []; }
  return (
    <div className="rec-badges">
      {item.window_used && <span className="win-chip">{item.window_used}</span>}
      {typeof item.final_score === 'number' && item.final_score > 0 && (
        <span className="rec-final">{item.final_score.toFixed(1)}점</span>
      )}
      {missing.map((badge) => <span className="warn-chip sm" key={badge}>{badge}</span>)}
    </div>
  );
}

function RecommendationsView({
  role,
  platform,
  editorScope,
  notify,
}: {
  role: Role;
  platform: PlatformFilter;
  editorScope: string;
  notify: (notice: Notice) => void;
}) {
  const [sort, setSort] = useState<'latest' | 'popular' | 'pinned'>('latest');
  const [refresh, setRefresh] = useState(0);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const [items, setItems] = useState<PostResult[]>([]);
  const [date, setDate] = useState(todayKST());
  const [manualUrl, setManualUrl] = useState('');
  const runs = useApiData(`recs-${refresh}`, api.recommendationRuns);
  // 사이트 전체가 editor_email 스코프: admin은 선택된 편집자의 run만 본다
  const scopedRuns = (runs.data?.runs || []).filter((run) => role !== 'admin' || !editorScope || run.editor_email === editorScope);
  const selectedRun = scopedRuns.find((run) => run.id === selectedRunId) || scopedRuns[0] || null;

  useEffect(() => {
    if (!selectedRun) {
      setItems([]);
      return;
    }
    let active = true;
    api.recommendationRun(selectedRun.id)
      .then((data) => { if (active) setItems(data.items || []); })
      .catch((error) => notify({ tone: 'error', text: error instanceof Error ? error.message : '추천 상세 불러오기 실패' }));
    return () => { active = false; };
  }, [selectedRun?.id, notify]);

  async function generate(force = false) {
    if (role === 'admin' && !editorScope) {
      notify({ tone: 'error', text: '편집자를 먼저 선택해주세요' });
      return;
    }
    try {
      const action = force ? api.regenerateRecommendation : api.generateRecommendation;
      const result = await action({ date, platform, categoryKey: 'all', editorEmail: editorScope });
      setSelectedRunId(result.run.id);
      setItems(result.items || []);
      setRefresh((value) => value + 1);
      notify({ tone: 'ok', text: force ? '재추천을 생성했습니다' : '추천을 생성했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '추천 생성 실패' });
    }
  }

  async function addManualItem() {
    if (!selectedRun) {
      notify({ tone: 'error', text: '먼저 추천 목록을 생성하거나 선택해주세요' });
      return;
    }
    const url = manualUrl.trim();
    if (!url) {
      notify({ tone: 'error', text: '추가할 URL을 입력해주세요' });
      return;
    }
    try {
      const result = await api.addRecommendationItem(selectedRun.id, { url });
      setSelectedRunId(result.run.id);
      setItems((prev) => [...prev.filter((item) => item.id !== result.item.id), result.item]
        .sort((a, b) => (a.rank || 0) - (b.rank || 0)));
      setManualUrl('');
      setRefresh((value) => value + 1);
      notify({ tone: 'ok', text: '추천에 게시물을 추가했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '게시물 추가 실패' });
    }
  }

  // 릴스/틱톡 추천 v1.0: [최신]/[인기] 탭은 서버가 저장한 item.tab으로 분리, 고정은 필터
  const byRank = (a: PostResult, b: PostResult) => (a.rank || 0) - (b.rank || 0);
  const visibleItems = sort === 'pinned'
    ? items.filter((item) => item.is_pinned).sort(byRank)
    : items.filter((item) => (item.tab || 'latest') === sort).sort(byRank);
  const shortage = sort !== 'pinned' && Boolean(selectedRun) && visibleItems.length > 0 && visibleItems.length < 7;

  async function togglePin(item: PostResult) {
    if (!item.id) return;
    await api.updateRecommendationItem(item.id, { isPinned: !item.is_pinned });
    setItems((prev) => prev.map((row) => row.id === item.id ? { ...row, is_pinned: row.is_pinned ? 0 : 1 } : row));
  }

  async function sendToExample(item: PostResult) {
    if (!item.id) return;
    await api.recommendationToExample(item.id);
    notify({ tone: 'ok', text: '예시에 등록했습니다' });
  }

  async function sendToArchive(item: PostResult) {
    if (!item.id) return;
    await api.recommendationToArchive(item.id);
    notify({ tone: 'ok', text: '확정에 등록했습니다' });
  }

  function runBadge(run: RecommendationRun) {
    if (run.status === 'done') return <span className="sbadge s-done">완료</span>;
    if (run.status === 'failed') return <span className="sbadge s-failed">실패</span>;
    return <span className="sbadge s-pending">{run.status || '대기'}</span>;
  }

  return (
    <section className="panel active" id="panelRec">
      <div className="rec-toolbar">
        <div className="rec-toolbar-actions">
          <div className="rec-sort-toggle">
            <button className={`rec-sort-btn ${sort === 'latest' ? 'active' : ''}`} onClick={() => setSort('latest')}>최신</button>
            <button className={`rec-sort-btn ${sort === 'popular' ? 'active' : ''}`} onClick={() => setSort('popular')}>인기</button>
            <button className={`rec-sort-btn ${sort === 'pinned' ? 'active' : ''}`} onClick={() => setSort('pinned')} data-rec-sort="pinned">고정</button>
          </div>
          {shortage && <span className="warn-chip">후보 부족 {visibleItems.length}/7</span>}
          <div className="rec-toolbar-right">
            {role === 'admin' && (
              <>
                <input className="rank-input date" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
                <button className="btn-secondary btn-sm" onClick={() => generate(true)}>↺ 재추천</button>
                <button className="expl-collect-btn" onClick={() => generate(false)}>추천 생성</button>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="rec-board">
        {runs.status === 'loading' && <EmptyPanel title="추천을 불러오는 중" />}
        {runs.status === 'ready' && !scopedRuns.length && (
          <EmptyPanel title={role === 'admin' && editorScope ? '이 편집자의 추천이 아직 없습니다' : '추천이 아직 없습니다'} />
        )}
        {scopedRuns.map((run) => {
          const open = selectedRun?.id === run.id;
          return (
            <section className={`rec-folder ${open ? 'open' : ''}`} key={run.id}>
              <button className="rec-folder-hdr" type="button" onClick={() => setSelectedRunId(open ? -1 : run.id)}>
                <div className="rec-folder-main">
                  <div>
                    <div className="rec-folder-name">{run.run_date}</div>
                    <div className="rec-folder-meta">
                      최신·인기 Top 7 · {run.generated_by === 'cron' ? '자동 생성' : '수동 생성'}
                      {(run.regenerate_count || 0) > 0 ? ` · 재추천 ${run.regenerate_count}회` : ''}
                    </div>
                  </div>
                </div>
                <div className="rec-folder-right">
                  {runBadge(run)}
                  <span className="rec-folder-arrow">›</span>
                </div>
              </button>
              {open && (
                <div className="rec-folder-body">
                  {role === 'admin' && (
                    <div className="rec-folder-tools">
                      <input
                        className="rec-link-input"
                        type="url"
                        value={manualUrl}
                        onChange={(event) => setManualUrl(event.target.value)}
                        onKeyDown={(event) => { if (event.key === 'Enter') void addManualItem(); }}
                        placeholder="이 폴더에 링크 추가 · Instagram 릴스 / TikTok 링크"
                      />
                      <button className="btn-primary btn-sm" type="button" onClick={addManualItem}>+ 링크</button>
                    </div>
                  )}
                  {sort === 'pinned' && !visibleItems.length ? (
                    <div className="pin-empty-state">
                      <b>고정된게 없습니다.</b>
                      <span>고정버튼을 눌러 고정해주세요.</span>
                    </div>
                  ) : !visibleItems.length ? (
                    <div className="pin-empty-state">
                      <b>{sort === 'latest' ? '최신' : '인기'} 추천이 없습니다.</b>
                      <span>수집 후 추천 생성을 실행해주세요.</span>
                    </div>
                  ) : (
                    <div className="rec-vid-list">
                      {visibleItems.map((item, index) => (
                        <div className={`rec-vid-item ${item.is_pinned ? 'is-pinned' : ''}`} key={item.id || index}>
                          <div className={`rec-rank-num ${index === 0 ? 'n1' : index === 1 ? 'n2' : index === 2 ? 'n3' : ''}`}>{index + 1}</div>
                          <a className="rec-thumb" href={postUrl(item)} target="_blank" rel="noreferrer">
                            {thumb(item) ? <img src={thumb(item)} alt="" /> : <span className="thumb-fallback">🎬</span>}
                          </a>
                          <div className="rec-vid-info">
                            <a className="rec-vid-title" href={postUrl(item)} target="_blank" rel="noreferrer">{captionPreview(item)}</a>
                            <a className="rec-vid-ch" href={accountUrl(item)} target="_blank" rel="noreferrer"><PlatformBadge platform={item.platform} /> {accountTitle(item)}</a>
                            <div className="rec-vid-stats">
                              <span className="c-blue">{formatNumber(viewCount(item))} 조회</span>
                              <span>좋아요 {formatNumber(item.likeCount ?? item.like_count)}</span>
                              <span>댓글 {formatNumber(item.commentCount ?? item.comment_count)}</span>
                              <span>공유 {formatNumber(item.shareCount ?? item.share_count)}</span>
                            </div>
                            <RecBadges item={item} />
                          </div>
                          <div className="rec-item-actions">
                            <button className={`rec-pin-btn ${item.is_pinned ? 'on' : ''}`} onClick={() => void togglePin(item)}>{item.is_pinned ? '고정됨' : '고정'}</button>
                            <button className="action-route-btn dup" onClick={() => void sendToExample(item)}>예시</button>
                            <button className="action-route-btn arc" onClick={() => void sendToArchive(item)}>확정</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </section>
  );
}

function ExamplesView({ notify }: { notify: (notice: Notice) => void }) {
  const [sort, setSort] = useState<'latest' | 'popular' | 'pinned'>('latest');
  const [accountFilter, setAccountFilter] = useState('');
  const [refresh, setRefresh] = useState(0);
  const examples = useApiData(`examples-${sort}-${refresh}`, () => api.examples(sort));
  const items = examples.data?.items || [];
  const accounts = Array.from(new Set(items.map((item) => item.username).filter((value): value is string => Boolean(value))));
  const visibleItems = accountFilter ? items.filter((item) => item.username === accountFilter) : items;

  async function togglePin(item: PostResult) {
    if (!item.id) return;
    await api.updateExample(item.id, { isPinned: !item.is_pinned });
    setRefresh((value) => value + 1);
    notify({ tone: 'ok', text: item.is_pinned ? '고정을 해제했습니다' : '고정했습니다' });
  }

  async function sendToArchive(item: PostResult) {
    await api.addArchive(postArchivePayload(item));
    notify({ tone: 'ok', text: '확정에 등록했습니다' });
  }

  async function remove(item: PostResult) {
    if (!item.id) return;
    await api.deleteExample(item.id);
    setRefresh((value) => value + 1);
    notify({ tone: 'ok', text: '예시에서 삭제했습니다' });
  }

  return (
    <section className="panel active" id="panelDup" data-ex-view="list">
      <SubTabs tabs={[['latest', '최신순'], ['popular', '인기순'], ['pinned', '고정']]} active={sort} onChange={(key) => setSort(key as typeof sort)} />
      <div className="content">
        {accounts.length > 1 && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' }}>
            <button className={`rank-chip ${!accountFilter ? 'on' : ''}`} onClick={() => setAccountFilter('')}>전체</button>
            {accounts.map((username) => (
              <button key={username} className={`rank-chip ${accountFilter === username ? 'on' : ''}`} onClick={() => setAccountFilter(username)}>@{username}</button>
            ))}
          </div>
        )}
        {examples.status === 'loading' && <EmptyPanel title="예시를 불러오는 중" />}
        {examples.status === 'ready' && sort === 'pinned' && !visibleItems.length && (
          <EmptyPanel title="고정된게 없습니다. 고정버튼을 눌러 고정해주세요." />
        )}
        {examples.status === 'ready' && sort !== 'pinned' && !visibleItems.length && (
          <EmptyPanel title="예시 게시물이 없습니다" />
        )}
        {visibleItems.length > 0 && (
          <div className="ex-list">
            {visibleItems.map((item) => (
              <div className="ex-vid-row" key={item.id}>
                <a className="ex-thumb" href={postUrl(item)} target="_blank" rel="noreferrer">
                  {thumb(item) ? <img src={thumb(item)} alt="" /> : <span className="thumb-fallback">🎬</span>}
                </a>
                <div className="ex-info">
                  <a className="ex-title row-caption" href={postUrl(item)} target="_blank" rel="noreferrer" style={{ display: 'block' }}>{captionPreview(item)}</a>
                  <a className="ex-ch" href={accountUrl(item)} target="_blank" rel="noreferrer" style={{ display: 'block' }}><PlatformBadge platform={item.platform} /> {accountTitle(item)}</a>
                  <div className="ex-stats">
                    <span className="c-blue">{formatNumber(viewCount(item))} 조회</span>
                    <span>{formatDate(item.uploadedAt || item.uploaded_at || item.published_at)}</span>
                  </div>
                  <div className="ex-acts">
                    <button className={`ex-pin-btn ${item.is_pinned ? 'on' : ''}`} onClick={() => void togglePin(item)}>{item.is_pinned ? '고정됨' : '고정'}</button>
                    <button className="action-route-btn arc" onClick={() => void sendToArchive(item)}>확정</button>
                    <button className="mini-btn danger" onClick={() => void remove(item)}>삭제</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ArchiveView({ notify }: { notify: (notice: Notice) => void }) {
  const [status, setStatus] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [eventsById, setEventsById] = useState<Record<number, ArchiveEvent[]>>({});
  const [noteDrafts, setNoteDrafts] = useState<Record<number, string>>({});
  const archive = useApiData(`archive-${status}-${refresh}`, () => api.archive(status ? { status } : {}));
  const items = archive.data?.items || [];
  const itemIdsKey = items.map((item) => item.id).filter(Boolean).join(',');

  useEffect(() => {
    setNoteDrafts((prev) => {
      const next = { ...prev };
      for (const item of items) {
        if (!item.id) continue;
        if (next[item.id] === undefined) next[item.id] = item.editor_note || '';
      }
      return next;
    });
  }, [itemIdsKey]);

  useEffect(() => {
    let active = true;
    const ids = items.map((item) => item.id).filter((id): id is number => Number.isFinite(id));
    if (!ids.length) {
      setEventsById({});
      return () => { active = false; };
    }
    Promise.all(ids.map(async (id) => {
      try {
        const result = await api.archiveEvents(id);
        return [id, result.events] as const;
      } catch {
        return [id, []] as const;
      }
    })).then((pairs) => {
      if (!active) return;
      setEventsById(Object.fromEntries(pairs));
    });
    return () => { active = false; };
  }, [itemIdsKey, refresh]);

  async function updateStatus(item: PostResult, nextStatus: string) {
    if (!item.id) return;
    await api.updateArchive(item.id, { status: nextStatus });
    setRefresh((value) => value + 1);
    notify({ tone: 'ok', text: '상태를 저장했습니다' });
  }

  async function saveNote(item: PostResult) {
    if (!item.id) return;
    await api.updateArchive(item.id, { editor_note: noteDrafts[item.id] || '' });
    setRefresh((value) => value + 1);
    notify({ tone: 'ok', text: '메모를 저장했습니다' });
  }

  async function unarchive(item: PostResult) {
    if (!item.id) return;
    if (!window.confirm('정말로 확정 해제하시겠어요?')) return;
    await api.deleteArchive(item.id);
    setRefresh((value) => value + 1);
    notify({ tone: 'ok', text: '확정을 해제했습니다' });
  }

  const ARCHIVE_STATUSES: Array<[string, string]> = [
    ['editing', '편집 중'],
    ['completed', '편집 완료'],
    ['revision_requested', '수정 요청'],
    ['revising', '수정 중'],
  ];

  return (
    <section className="panel active">
      <div className="sub-tabs">
        {[['', '전체'], ...ARCHIVE_STATUSES].map(([key, label]) => (
          <button key={key} className={`sub-tab ${status === key ? 'active' : ''}`} onClick={() => setStatus(key)}>{label}</button>
        ))}
        <div className="sub-r"><span className="sec-meta">{items.length}개</span></div>
      </div>
      <div className="content">
        {archive.status === 'loading' && <EmptyPanel title="확정을 불러오는 중" />}
        {archive.status === 'ready' && !items.length && <EmptyPanel title="확정된 게시물이 없습니다" />}
        <div className="arc-dynamic-list">
          {items.map((item) => {
            const events = (item.id && eventsById[item.id]) || [];
            const badgeClass = item.status === 'completed' ? 's-done' : item.status === 'revision_requested' ? 's-purple' : 's-editing';
            return (
              <div className="arc-card" key={item.id}>
                <button className="arc-corner-unarchive" onClick={() => void unarchive(item)}>확정 해제</button>
                <div className="arc-top">
                  <a className="arc-thumb" href={postUrl(item)} target="_blank" rel="noreferrer">
                    {thumb(item) ? <img src={thumb(item)} alt="" /> : <span className="thumb-fallback">🎬</span>}
                  </a>
                  <div className="arc-info">
                    <span className={`sbadge ${badgeClass}`}>{archiveStatusLabel(item.status)}</span> <PlatformBadge platform={item.platform} />
                    <a className="arc-title row-caption" href={postUrl(item)} target="_blank" rel="noreferrer" style={{ display: 'block', marginTop: '5px' }}>{captionPreview(item)}</a>
                    <a className="arc-ch" href={accountUrl(item)} target="_blank" rel="noreferrer">{accountTitle(item)}</a>
                    <div className="arc-stats">
                      <span>조회수 {formatNumber(viewCount(item))}</span>
                      <span>댓글수 {formatNumber(item.commentCount ?? item.comment_count)}</span>
                      <span>좋아요수 {formatNumber(item.likeCount ?? item.like_count)}</span>
                      <span>공유수 {formatNumber(item.shareCount ?? item.share_count)}</span>
                    </div>
                    <div className="arc-history">
                      {item.archived_at && <span className="arc-log">확정 {formatUnixTime(item.archived_at)}</span>}
                      {events.slice(-3).map((event) => (
                        <span className="arc-log" key={event.id}>{archiveEventLabel(event)} {formatUnixTime(event.created_at)}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="arc-edit">
                  <select
                    className="status-sel"
                    value={item.status || 'editing'}
                    onChange={(event) => void updateStatus(item, event.target.value)}
                  >
                    {ARCHIVE_STATUSES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                  </select>
                  <textarea
                    className="note-inp"
                    placeholder="작업 메모"
                    value={(item.id != null ? noteDrafts[item.id] : undefined) ?? item.editor_note ?? ''}
                    onChange={(event) => {
                      if (item.id == null) return;
                      setNoteDrafts((prev) => ({ ...prev, [item.id as number]: event.target.value }));
                    }}
                  />
                  <button className="btn-save" onClick={() => void saveNote(item)}>저장</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function AccountsView({ platform, notify }: { platform: PlatformFilter; notify: (notice: Notice) => void }) {
  const [platformSel, setPlatformSel] = useState<PlatformFilter>(platform);
  const [sort, setSort] = useState('followers');
  const [query, setQuery] = useState('');
  const [view, setView] = useState<'card' | 'table'>('table');
  const [openAccount, setOpenAccount] = useState<OpenAccountRef | null>(null);
  const [accounts, setAccounts] = useState<AccountResult[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 30;

  async function load(nextOffset: number, append: boolean) {
    setLoading(true);
    try {
      const data = await api.socialAccounts({ platform: platformSel === 'all' ? undefined : platformSel, q: query.trim() || undefined, sort, offset: nextOffset, limit });
      setAccounts((prev) => (append ? [...prev, ...(data.accounts || [])] : (data.accounts || [])));
      setTotal(data.total || 0);
      setOffset(nextOffset);
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '계정 조회 실패' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformSel, sort]);

  function openPosts(account: AccountResult) {
    setOpenAccount({ platform: account.platform, username: account.username, accountId: account.accountId || account.account_id });
  }

  if (openAccount) {
    return <AccountPostsPanel account={openAccount} onBack={() => setOpenAccount(null)} notify={notify} />;
  }

  return (
    <section className="panel active">
      <div className="content">
        <div className="filter-band">
          <Select value={platformSel} onChange={(value) => setPlatformSel(value as PlatformFilter)} options={platformOptions} />
          <Select value={sort} onChange={setSort} options={accountSortOptions} wide />
          <input
            className="rank-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') void load(0, false); }}
            placeholder="계정 검색"
          />
          <button className="btn-secondary btn-sm" onClick={() => void load(0, false)}>검색</button>
          <div className="toolbar-right"><ViewToggle value={view} onChange={setView} /></div>
        </div>
        {loading && !accounts.length ? <EmptyPanel title="계정을 불러오는 중" /> : (
          <AccountResults items={accounts} view={view} onOpenPosts={openPosts} />
        )}
        {accounts.length > 0 && accounts.length < total && (
          <div className="rank-limit-controls">
            <span className="rank-limit-note">{accounts.length} / {total}개 표시 중</span>
            <button className="rank-more-btn" disabled={loading} onClick={() => void load(offset + limit, true)}>{loading ? '불러오는 중…' : '더 보기'}</button>
          </div>
        )}
      </div>
    </section>
  );
}

function TrendsView({
  platform,
  setActiveTab,
  notify,
}: {
  platform: PlatformFilter;
  setActiveTab: (key: string) => void;
  notify: (notice: Notice) => void;
}) {
  const [type, setType] = useState<TrendKeywordType>('hashtag');
  const [platformSel, setPlatformSel] = useState<PlatformFilter>(platform);
  const [timeWindow, setTimeWindow] = useState('24h');
  const [crossOnly, setCrossOnly] = useState(false);
  const state = useApiData(
    `trends-${type}-${platformSel}-${timeWindow}`,
    () => api.socialKeywords({ platform: platformSel === 'all' ? undefined : platformSel, type, window: timeWindow }),
  );
  const items = (state.data?.keywords || []).filter((keyword) => !crossOnly || keyword.crossPlatform || keyword.cross_platform);

  function goExplore(keyword: string) {
    try { sessionStorage.setItem('pick7reels.exploreKeyword', keyword); } catch { /* 세션 스토리지 접근 불가 환경은 무시한다. */ }
    setActiveTab('explore');
  }

  async function registerAsset(keyword: KeywordTrend) {
    try {
      await api.createAsset({
        target: 'recommendation',
        conditionType: 'keyword',
        label: `트렌드 · ${keyword.keyword}`,
        normalizedKey: keyword.keyword,
        source: 'social_trend',
        country: '',
        categoryKey: 'all',
        platform: keyword.platform === 'all' ? 'instagram' : keyword.platform,
        limitCount: 20,
        payload: { keyword: keyword.keyword, type: keyword.type },
      });
      notify({ tone: 'ok', text: '추천 에셋에 등록했습니다 · 에셋 화면에서 편집자를 지정해주세요' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '에셋 등록 실패' });
    }
  }

  return (
    <section className="panel active">
      <SubTabs tabs={trendSubtabs} active={type} onChange={(key) => setType(key as TrendKeywordType)} />
      <div className="content">
        <div className="filter-band">
          <Select value={platformSel} onChange={(value) => setPlatformSel(value as PlatformFilter)} options={platformOptions} />
          <Select value={timeWindow} onChange={setTimeWindow} options={trendWindowOptions} />
          <label className="sp-check-row"><input type="checkbox" checked={crossOnly} onChange={(event) => setCrossOnly(event.target.checked)} /><span>교차 플랫폼만</span></label>
        </div>
        {state.status === 'loading' && <EmptyPanel title="트렌드를 불러오는 중" />}
        {state.status === 'error' && <ErrorBox text={state.error} />}
        {state.status === 'ready' && !items.length && <EmptyPanel title="집계된 트렌드가 없습니다" />}
        {items.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>키워드</th><th>플랫폼</th><th>빈도</th><th>평균 점수</th><th>총 조회수</th><th>24h 성장</th><th>작업</th>
                </tr>
              </thead>
              <tbody>
                {items.map((keyword) => {
                  const growth = keyword.growth24h ?? keyword.growth_24h ?? 0;
                  return (
                    <tr key={`${keyword.keyword}-${keyword.type}-${keyword.platform}`}>
                      <td className="row-title">{keyword.keyword}</td>
                      <td>
                        {keyword.platform !== 'all' ? <PlatformBadge platform={keyword.platform} /> : '전체'}
                        {(keyword.crossPlatform || keyword.cross_platform) ? <span className="xplat-chip" style={{ marginLeft: '4px' }}>교차</span> : null}
                      </td>
                      <td className="num">{formatNumber(keyword.frequency)}</td>
                      <td className="num gold">{formatMetric(keyword.avgScore ?? keyword.avg_score)}</td>
                      <td className="num blue">{formatNumber(keyword.totalViews ?? keyword.total_views)}</td>
                      <td className={growth >= 0 ? 'trend-up' : 'trend-down'}>{growth >= 0 ? '▲' : '▼'}{Math.abs(Math.round(growth))}%</td>
                      <td>
                        <div className="row-actions">
                          <button className="mini-btn" onClick={() => goExplore(keyword.keyword)}>탐색으로 이동</button>
                          <button className="mini-btn green" onClick={() => void registerAsset(keyword)}>에셋 등록</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function GuidelinesView({ role, notify }: { role: Role; notify: (notice: Notice) => void }) {
  const [section, setSection] = useState<'steps' | 'tools'>('steps');
  const [refresh, setRefresh] = useState(0);
  const guidelines = useApiData(`guidelines-${refresh}`, api.guidelines);
  const steps = guidelines.data?.steps || [];
  const folders = guidelines.data?.toolFolders || [];
  const reload = () => setRefresh((value) => value + 1);

  return (
    <section className="panel active" id="panelTools">
      <SubTabs tabs={[['steps', '편집 순서'], ['tools', '도구 모음']]} active={section} onChange={(key) => setSection(key as typeof section)} />
      <div className="guide-stage">
        {guidelines.status === 'loading' || guidelines.status === 'error' ? (
          <DataState state={guidelines} emptyTitle="지침이 없습니다" />
        ) : section === 'steps' ? (
          <>
            <div className="step-flow">
              {steps.map((step, index) => (
                <StepNode key={step.id} step={step} index={index} />
              ))}
            </div>
            {role === 'admin' && (
              <GuidelineStepAdmin
                steps={steps}
                onSaved={reload}
                notify={notify}
              />
            )}
          </>
        ) : (
          <>
            <div className="tool-folder-row">
              {folders.map((folder) => <ToolFolder key={folder.id} folder={folder} />)}
            </div>
            {role === 'admin' && (
              <GuidelineToolAdmin
                folders={folders}
                onSaved={reload}
                notify={notify}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
}

function GuidelineStepAdmin({
  steps,
  onSaved,
  notify,
}: {
  steps: GuidelineStep[];
  onSaved: () => void;
  notify: (notice: Notice) => void;
}) {
  return (
    <div className="guide-editor">
      <div className="guide-editor-title">편집 순서 관리</div>
      {steps.map((step) => (
        <GuidelineStepEditRow key={step.id} step={step} onSaved={onSaved} notify={notify} />
      ))}
      <NewGuidelineStep nextStepNo={steps.length + 1} onSaved={onSaved} notify={notify} />
    </div>
  );
}

function GuidelineStepEditRow({
  step,
  onSaved,
  notify,
}: {
  step: GuidelineStep;
  onSaved: () => void;
  notify: (notice: Notice) => void;
}) {
  const [title, setTitle] = useState(step.title);
  const [description, setDescription] = useState(step.description);
  const [url, setUrl] = useState(step.url);
  const [buttonLabel, setButtonLabel] = useState(step.button_label);
  const [adminNote, setAdminNote] = useState(step.admin_note);
  const [enabled, setEnabled] = useState(Boolean(step.is_enabled));

  async function save() {
    try {
      await api.updateGuidelineStep(step.id, {
        title,
        description,
        url,
        buttonLabel,
        adminNote,
        isEnabled: enabled,
      });
      onSaved();
      notify({ tone: 'ok', text: '지침 스텝을 저장했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '지침 저장 실패' });
    }
  }

  return (
    <div className="guide-edit-row">
      <input className="rank-input tiny" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="제목" />
      <input className="rank-input grow" value={description} onChange={(event) => setDescription(event.target.value)} placeholder="설명" />
      <input className="rank-input grow" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="링크" />
      <input className="rank-input tiny" value={buttonLabel} onChange={(event) => setButtonLabel(event.target.value)} placeholder="버튼" />
      <input className="rank-input grow" value={adminNote} onChange={(event) => setAdminNote(event.target.value)} placeholder="관리자 메모" />
      <label className="guide-check"><input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} /> 사용</label>
      <button className="mini-btn green" onClick={save}>저장</button>
    </div>
  );
}

function NewGuidelineStep({
  nextStepNo,
  onSaved,
  notify,
}: {
  nextStepNo: number;
  onSaved: () => void;
  notify: (notice: Notice) => void;
}) {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');

  async function create() {
    if (!title.trim()) return;
    try {
      await api.createGuidelineStep({
        stepNo: nextStepNo,
        title: title.trim(),
        description: '',
        url,
        buttonLabel: '열기',
        sortOrder: nextStepNo,
      });
      setTitle('');
      setUrl('');
      onSaved();
      notify({ tone: 'ok', text: '지침 스텝을 추가했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '스텝 추가 실패' });
    }
  }

  return (
    <div className="guide-edit-row">
      <input className="rank-input grow" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="새 스텝 제목" />
      <input className="rank-input grow" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="링크" />
      <button className="mini-btn green" onClick={create} disabled={!title.trim()}>스텝 추가</button>
    </div>
  );
}

function GuidelineToolAdmin({
  folders,
  onSaved,
  notify,
}: {
  folders: GuidelineFolder[];
  onSaved: () => void;
  notify: (notice: Notice) => void;
}) {
  return (
    <div className="guide-editor">
      <div className="guide-editor-title">도구 모음 관리</div>
      {folders.map((folder) => (
        <GuidelineFolderEditBlock key={folder.id} folder={folder} onSaved={onSaved} notify={notify} />
      ))}
      <NewGuidelineFolder onSaved={onSaved} notify={notify} />
    </div>
  );
}

function GuidelineFolderEditBlock({
  folder,
  onSaved,
  notify,
}: {
  folder: GuidelineFolder;
  onSaved: () => void;
  notify: (notice: Notice) => void;
}) {
  const [title, setTitle] = useState(folder.title);
  const [description, setDescription] = useState(folder.description);
  const [enabled, setEnabled] = useState(Boolean(folder.is_enabled));

  async function saveFolder() {
    try {
      await api.updateToolFolder(folder.id, { title, description, isEnabled: enabled });
      onSaved();
      notify({ tone: 'ok', text: '도구 폴더를 저장했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '폴더 저장 실패' });
    }
  }

  return (
    <div className="guide-folder-edit">
      <div className="guide-edit-row">
        <input className="rank-input tiny" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="폴더명" />
        <input className="rank-input grow" value={description} onChange={(event) => setDescription(event.target.value)} placeholder="설명" />
        <label className="guide-check"><input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} /> 사용</label>
        <button className="mini-btn green" onClick={saveFolder}>폴더 저장</button>
      </div>
      {folder.tools.map((tool) => (
        <GuidelineToolEditRow key={tool.id} tool={tool} onSaved={onSaved} notify={notify} />
      ))}
      <NewGuidelineTool folderId={folder.id} onSaved={onSaved} notify={notify} />
    </div>
  );
}

function GuidelineToolEditRow({
  tool,
  onSaved,
  notify,
}: {
  tool: GuidelineTool;
  onSaved: () => void;
  notify: (notice: Notice) => void;
}) {
  const [title, setTitle] = useState(tool.title);
  const [url, setUrl] = useState(tool.url);
  const [buttonLabel, setButtonLabel] = useState(tool.button_label);
  const [adminNote, setAdminNote] = useState(tool.admin_note);
  const [enabled, setEnabled] = useState(Boolean(tool.is_enabled));

  async function save() {
    try {
      await api.updateGuidelineTool(tool.id, { title, url, buttonLabel, adminNote, isEnabled: enabled });
      onSaved();
      notify({ tone: 'ok', text: '도구를 저장했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '도구 저장 실패' });
    }
  }

  return (
    <div className="guide-edit-row tool">
      <input className="rank-input tiny" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="도구명" />
      <input className="rank-input grow" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="링크" />
      <input className="rank-input tiny" value={buttonLabel} onChange={(event) => setButtonLabel(event.target.value)} placeholder="버튼" />
      <input className="rank-input grow" value={adminNote} onChange={(event) => setAdminNote(event.target.value)} placeholder="관리자 메모" />
      <label className="guide-check"><input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} /> 사용</label>
      <button className="mini-btn green" onClick={save}>저장</button>
    </div>
  );
}

function NewGuidelineTool({
  folderId,
  onSaved,
  notify,
}: {
  folderId: number;
  onSaved: () => void;
  notify: (notice: Notice) => void;
}) {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');

  async function create() {
    if (!title.trim()) return;
    try {
      await api.createGuidelineTool({ folderId, title: title.trim(), url, buttonLabel: '열기' });
      setTitle('');
      setUrl('');
      onSaved();
      notify({ tone: 'ok', text: '도구를 추가했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '도구 추가 실패' });
    }
  }

  return (
    <div className="guide-edit-row tool">
      <input className="rank-input grow" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="새 도구명" />
      <input className="rank-input grow" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="링크" />
      <button className="mini-btn green" onClick={create} disabled={!title.trim()}>도구 추가</button>
    </div>
  );
}

function NewGuidelineFolder({
  onSaved,
  notify,
}: {
  onSaved: () => void;
  notify: (notice: Notice) => void;
}) {
  const [title, setTitle] = useState('');

  async function create() {
    if (!title.trim()) return;
    try {
      await api.createToolFolder({ title: title.trim(), description: '' });
      setTitle('');
      onSaved();
      notify({ tone: 'ok', text: '도구 폴더를 추가했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '폴더 추가 실패' });
    }
  }

  return (
    <div className="guide-edit-row">
      <input className="rank-input grow" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="새 폴더명" />
      <button className="mini-btn green" onClick={create} disabled={!title.trim()}>폴더 추가</button>
    </div>
  );
}

function StepNode({ step, index }: { step: GuidelineStep; index: number }) {
  return (
    <div className="step-node">
      <div className="step-num">{step.step_no}</div>
      {index < 4 && <div className="step-line" />}
      <div className="step-card">
        <div className="step-title">{step.title}</div>
        <div className="step-desc">{step.description}</div>
        {step.admin_note && <div className="guide-note">{step.admin_note}</div>}
        {step.url && <a className="btn-secondary btn-sm" href={step.url} target="_blank" rel="noreferrer">{step.button_label}</a>}
      </div>
    </div>
  );
}

function ToolFolder({ folder }: { folder: GuidelineFolder }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`tool-folder ${open ? 'open' : ''}`}>
      <button className="tool-folder-head" onClick={() => setOpen((value) => !value)}>
        <span>{folder.title}</span>
        <span>{open ? '접기' : '열기'}</span>
      </button>
      {open && (
        <div className="tool-list">
          {folder.tools.length ? folder.tools.map((tool) => (
            <div className="tool-item" key={tool.id}>
              <a className="tool-link" href={tool.url} target="_blank" rel="noreferrer">{tool.title}</a>
              {tool.admin_note && <div className="guide-note">{tool.admin_note}</div>}
            </div>
          )) : <div className="empty-meta">등록된 도구가 없습니다</div>}
        </div>
      )}
    </div>
  );
}

function AccountResults({
  items,
  view,
  onOpenPosts,
}: {
  items: AccountResult[];
  view: 'card' | 'table';
  onOpenPosts: (account: AccountResult) => void;
}) {
  if (!items.length) return <EmptyPanel title="계정 결과가 없습니다" />;
  if (view === 'card') {
    return (
      <div className="channel-card-list">
        {items.map((account) => (
          <div className="channel-card" key={account.accountId || account.account_id}>
            <div className="rank-badge">#{account.rank || '-'}</div>
            <a className="channel-avatar" href={accountUrl(account)} target="_blank" rel="noreferrer">
              {account.avatar ? <img src={account.avatar} alt="" /> : <span>👤</span>}
            </a>
            <div className="channel-main">
              <div className="channel-title"><PlatformBadge platform={account.platform} /> {accountTitle(account)}</div>
              <div className="channel-meta">
                팔로워 {formatNumber(account.followerCount ?? account.follower_count)} · 평균조회수 {formatNumber(account.avgViewsRecent ?? account.avg_views_recent)} · 참여율 {formatMetric(account.engagementRate ?? account.engagement_rate)}% · 게시물 {formatNumber(account.postCount ?? account.post_count)}
              </div>
              <RecentPostStrip posts={account.recentPosts || []} />
            </div>
            <div className="row-actions">
              <button className="mini-btn green" onClick={() => onOpenPosts(account)}>계정 게시물</button>
              <a className="mini-btn" href={accountUrl(account)} target="_blank" rel="noreferrer">프로필</a>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>순위</th>
            <th>계정 정보</th>
            <th>팔로워</th>
            <th>평균조회수</th>
            <th>참여율</th>
            <th>게시물수</th>
            <th>최근 게시물</th>
            <th>작업</th>
          </tr>
        </thead>
        <tbody>
          {items.map((account) => (
            <tr key={account.accountId || account.account_id}>
              <td>#{account.rank || '-'}</td>
              <td>
                <a className="inline-media" href={accountUrl(account)} target="_blank" rel="noreferrer">
                  {account.avatar ? <img src={account.avatar} alt="" /> : <span>👤</span>}
                  <span><PlatformBadge platform={account.platform} /> {accountTitle(account)}</span>
                </a>
              </td>
              <td>{formatNumber(account.followerCount ?? account.follower_count)}</td>
              <td>{formatNumber(account.avgViewsRecent ?? account.avg_views_recent)}</td>
              <td>{formatMetric(account.engagementRate ?? account.engagement_rate)}%</td>
              <td>{formatNumber(account.postCount ?? account.post_count)}</td>
              <td><RecentPostStrip posts={account.recentPosts || []} compact /></td>
              <td>
                <div className="row-actions">
                  <button className="mini-btn green" onClick={() => onOpenPosts(account)}>계정 게시물</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecentPostStrip({ posts, compact = false }: { posts: RecentPost[]; compact?: boolean }) {
  if (!posts.length) return <span className="empty-meta">최근 게시물 없음</span>;
  return (
    <div className={`recent-strip ${compact ? 'compact' : ''}`}>
      {posts.slice(0, 3).map((post) => (
        <a key={post.postId} className="recent-thumb" href={post.originalUrl || '#'} target="_blank" rel="noreferrer" title={post.caption}>
          {post.thumbnail ? <img src={post.thumbnail} alt="" /> : <span className="thumb-fallback">🎬</span>}
        </a>
      ))}
    </div>
  );
}

function PostMetricStrip({ item }: { item: PostResult }) {
  return (
    <div className="metric-strip">
      <span>조회수 <b>{formatNumber(viewCount(item))}</b></span>
      <span>좋아요 <b>{formatNumber(item.likeCount ?? item.like_count)}</b></span>
      <span>댓글 <b>{formatNumber(item.commentCount ?? item.comment_count)}</b></span>
      <span>{hoursAgo(item.uploadedAt || item.uploaded_at || item.published_at)}</span>
    </div>
  );
}

function PostResults({
  items,
  view,
  mode,
  emptyTitle = '게시물이 없습니다',
  onPin,
  onExample,
  onArchive,
  onDelete,
  onStatus,
  onOpenAccount,
  archiveEvents,
  noteDrafts,
  onNoteChange,
  onNoteSave,
}: {
  items: PostResult[];
  view: 'card' | 'table';
  mode: 'explore' | 'collection' | 'recommendation' | 'example' | 'archive';
  emptyTitle?: string;
  onPin?: (item: PostResult) => void | Promise<void>;
  onExample?: (item: PostResult) => void | Promise<void>;
  onArchive?: (item: PostResult) => void | Promise<void>;
  onDelete?: (item: PostResult) => void | Promise<void>;
  onStatus?: (item: PostResult, status: string) => void | Promise<void>;
  onOpenAccount?: (item: PostResult) => void | Promise<void>;
  archiveEvents?: Record<number, ArchiveEvent[]>;
  noteDrafts?: Record<number, string>;
  onNoteChange?: (item: PostResult, value: string) => void;
  onNoteSave?: (item: PostResult) => void | Promise<void>;
}) {
  const showInternalMetrics = mode === 'explore' || mode === 'collection';
  if (!items.length) return <EmptyPanel title={emptyTitle} />;
  if (view === 'card') {
    return (
      <div className="post-grid">
        {items.map((item, index) => (
          <article className="post-card" key={postId(item) || item.id || index}>
            <a className="post-thumb" href={postUrl(item)} target="_blank" rel="noreferrer">
              {thumb(item) ? <img src={thumb(item)} alt="" /> : <span className="thumb-fallback">🎬</span>}
              <span style={{ position: 'absolute', top: 8, left: 8 }}><PlatformBadge platform={item.platform} /></span>
              {showInternalMetrics && <span className="score-badge">★ {formatViralScore(item)}</span>}
            </a>
            <span className="post-title post-caption">{captionPreview(item, 60)}</span>
            <a className="post-account" href={accountUrl(item)} target="_blank" rel="noreferrer">{accountTitle(item)}</a>
            {mode === 'recommendation' && <RecBadges item={item} />}
            <PostMetricStrip item={item} />
            <PostActions
              mode={mode}
              item={item}
              onPin={onPin}
              onExample={onExample}
              onArchive={onArchive}
              onDelete={onDelete}
              onStatus={onStatus}
              onOpenAccount={onOpenAccount}
              events={item.id ? archiveEvents?.[item.id] : undefined}
              noteValue={item.id ? noteDrafts?.[item.id] : undefined}
              onNoteChange={onNoteChange}
              onNoteSave={onNoteSave}
            />
          </article>
        ))}
      </div>
    );
  }
  return (
    <div className="table-wrap">
      <table className="data-table video">
        <thead>
          <tr>
            <th>순위</th>
            <th>미리보기</th>
            <th>플랫폼</th>
            <th>캡션 / 계정</th>
            {showInternalMetrics && <th>바이럴 점수</th>}
            <th>조회수</th>
            <th>좋아요</th>
            <th>댓글</th>
            <th>공유</th>
            <th>저장수</th>
            {showInternalMetrics && <th>참여율</th>}
            {showInternalMetrics && <th>팔로워</th>}
            <th>업로드</th>
            <th>원본</th>
            <th>작업</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={postId(item) || item.id || index}>
              <td>#{item.rank || index + 1}</td>
              <td>
                <a className="mini-thumb v" href={postUrl(item)} target="_blank" rel="noreferrer">
                  {thumb(item) ? <img src={thumb(item)} alt="" /> : <span className="thumb-fallback">🎬</span>}
                </a>
              </td>
              <td><PlatformBadge platform={item.platform} /></td>
              <td>
                <span className="row-title row-caption" style={{ display: 'block' }}>{captionPreview(item, 70)}</span>
                <a className="row-sub" href={accountUrl(item)} target="_blank" rel="noreferrer">{accountTitle(item)}</a>
                {mode === 'recommendation' && <RecBadges item={item} />}
              </td>
              {showInternalMetrics && <td><span className="score-pill">★ {formatViralScore(item)}</span></td>}
              <td className="num blue">{formatNumber(viewCount(item))}</td>
              <td>{formatNumber(item.likeCount ?? item.like_count)}</td>
              <td>{formatNumber(item.commentCount ?? item.comment_count)}</td>
              <td>{formatNumber(item.shareCount ?? item.share_count)}</td>
              <td>{formatNumber(item.saveCount ?? item.save_count)}</td>
              {showInternalMetrics && <td>{formatMetric(item.engagementRate ?? item.engagement_rate)}%</td>}
              {showInternalMetrics && <td className="num cyan">{formatNumber(item.followerCount ?? item.follower_count)}</td>}
              <td>{hoursAgo(item.uploadedAt || item.uploaded_at || item.published_at)}</td>
              <td><a className="mini-btn" href={postUrl(item)} target="_blank" rel="noreferrer">↗</a></td>
              <td>
                <PostActions
                  mode={mode}
                  item={item}
                  onPin={onPin}
                  onExample={onExample}
                  onArchive={onArchive}
                  onDelete={onDelete}
                  onStatus={onStatus}
                  onOpenAccount={onOpenAccount}
                  events={item.id ? archiveEvents?.[item.id] : undefined}
                  noteValue={item.id ? noteDrafts?.[item.id] : undefined}
                  onNoteChange={onNoteChange}
                  onNoteSave={onNoteSave}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PostActions({
  mode,
  item,
  onPin,
  onExample,
  onArchive,
  onDelete,
  onStatus,
  onOpenAccount,
  events,
  noteValue,
  onNoteChange,
  onNoteSave,
}: {
  mode: string;
  item: PostResult;
  onPin?: (item: PostResult) => void | Promise<void>;
  onExample?: (item: PostResult) => void | Promise<void>;
  onArchive?: (item: PostResult) => void | Promise<void>;
  onDelete?: (item: PostResult) => void | Promise<void>;
  onStatus?: (item: PostResult, status: string) => void | Promise<void>;
  onOpenAccount?: (item: PostResult) => void | Promise<void>;
  events?: ArchiveEvent[];
  noteValue?: string;
  onNoteChange?: (item: PostResult, value: string) => void;
  onNoteSave?: (item: PostResult) => void | Promise<void>;
}) {
  return (
    <div className={mode === 'archive' ? 'archive-tools' : 'row-actions'}>
      <div className="row-actions">
        {onOpenAccount && <button className="mini-btn" onClick={() => onOpenAccount(item)}>계정 게시물</button>}
        {onExample && <button className="mini-btn purple" onClick={() => onExample(item)}>예시</button>}
        {onArchive && <button className="mini-btn brown" onClick={() => onArchive(item)}>확정</button>}
        {onPin && <button className={`mini-btn ${item.is_pinned ? 'purple active' : ''}`} onClick={() => onPin(item)}>{item.is_pinned ? '고정됨' : '고정'}</button>}
        {mode === 'archive' && onStatus && (
          <>
            <button className={`mini-btn ${item.status === 'editing' ? 'green active' : ''}`} onClick={() => onStatus(item, 'editing')}>편집 중</button>
            <button className={`mini-btn ${item.status === 'completed' ? 'green active' : ''}`} onClick={() => onStatus(item, 'completed')}>편집 완료</button>
            <button className={`mini-btn ${item.status === 'revision_requested' ? 'green active' : ''}`} onClick={() => onStatus(item, 'revision_requested')}>수정 요청</button>
            <button className={`mini-btn ${item.status === 'revising' ? 'green active' : ''}`} onClick={() => onStatus(item, 'revising')}>수정 중</button>
          </>
        )}
        {onDelete && <button className="mini-btn danger" onClick={() => onDelete(item)}>{mode === 'archive' ? '확정 해제' : '삭제'}</button>}
      </div>
      {mode === 'archive' && (
        <>
          <div className="archive-note-row">
            <textarea
              className="archive-note"
              value={noteValue ?? item.editor_note ?? ''}
              onChange={(event) => onNoteChange?.(item, event.target.value)}
              placeholder="작업 메모"
            />
            <button className="mini-btn green" onClick={() => onNoteSave?.(item)}>저장</button>
          </div>
          <ArchiveHistory item={item} events={events || []} />
        </>
      )}
    </div>
  );
}

function ArchiveHistory({ item, events }: { item: PostResult; events: ArchiveEvent[] }) {
  const lines = [
    item.archived_at ? `확정 ${formatUnixTime(item.archived_at)}` : '',
    ...events.slice(-3).map((event) => `${archiveEventLabel(event)} ${formatUnixTime(event.created_at)}`),
  ].filter(Boolean);
  if (!lines.length) return null;
  return (
    <div className="archive-history">
      {lines.map((line, index) => <span key={`${line}-${index}`}>{line}</span>)}
    </div>
  );
}

function SettingsPanel({
  devEmail,
  setDevEmail,
  role,
  notify,
  onClose,
}: {
  devEmail: string;
  setDevEmail: (email: string) => void;
  role: Role;
  notify: (notice: Notice) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(devEmail);
  const [keys, setKeys] = useState<Array<{ id: number; label: string; status: string; maskedKey: string; storage: string }>>([]);
  const [keyDraft, setKeyDraft] = useState('');
  const [keyLabel, setKeyLabel] = useState('Apify API 토큰');
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [settingsCategories, setSettingsCategories] = useState<Category[]>([]);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState<Role>('editor');
  const [permissionUserId, setPermissionUserId] = useState('');
  const [permissionCategory, setPermissionCategory] = useState('all');
  const [permissionPlatform, setPermissionPlatform] = useState<Platform>('instagram');
  const [adminUrl, setAdminUrl] = useState('/admin');
  const [editorUrl, setEditorUrl] = useState('/editor');
  const [geminiEnabled, setGeminiEnabled] = useState(false);

  useEffect(() => {
    if (role !== 'admin') return;
    let active = true;
    Promise.allSettled([api.keys(), api.users(), api.categories(), api.settings()])
      .then(([keyResult, userResult, categoryResult, settingsResult]) => {
        if (!active) return;
        setKeys(keyResult.status === 'fulfilled' ? keyResult.value : []);
        setUsers(userResult.status === 'fulfilled' ? userResult.value.users : []);
        setSettingsCategories(categoryResult.status === 'fulfilled' ? categoryResult.value.categories : []);
        if (settingsResult.status === 'fulfilled') {
          const settings = settingsResult.value.settings || {};
          const uiUrls = (settings['ui.urls'] || {}) as { admin?: string; editor?: string };
          const gemini = settings['gemini.enabled'] as boolean | { enabled?: boolean } | undefined;
          setAdminUrl(uiUrls.admin || '/admin');
          setEditorUrl(uiUrls.editor || '/editor');
          setGeminiEnabled(typeof gemini === 'boolean' ? gemini : Boolean(gemini?.enabled));
        }
      });
    return () => { active = false; };
  }, [role]);

  useEffect(() => {
    if (permissionUserId || !users.length) return;
    const firstEditor = users.find((user) => user.role === 'editor') || users[0];
    if (firstEditor) setPermissionUserId(String(firstEditor.id));
  }, [permissionUserId, users]);

  async function reloadUsers() {
    const data = await api.users();
    setUsers(data.users);
  }

  async function saveKey() {
    try {
      const saved = await api.createKey({ key: keyDraft.trim(), label: keyLabel.trim() || 'Apify API 토큰' });
      setKeys((rows) => [saved, ...rows.filter((row) => row.id !== saved.id)]);
      setKeyDraft('');
      notify({ tone: 'ok', text: 'API 토큰을 암호화해서 저장했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : 'API 토큰 저장 실패' });
    }
  }

  async function saveAppSettings() {
    try {
      await Promise.all([
        api.updateSetting('ui.urls', { admin: adminUrl.trim() || '/admin', editor: editorUrl.trim() || '/editor' }),
        api.updateSetting('gemini.enabled', { enabled: geminiEnabled }),
      ]);
      notify({ tone: 'ok', text: '설정을 저장했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '설정 저장 실패' });
    }
  }

  async function createUser() {
    try {
      await api.createUser({ email: newUserEmail.trim(), role: newUserRole });
      setNewUserEmail('');
      await reloadUsers();
      notify({ tone: 'ok', text: '사용자를 추가했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '사용자 추가 실패' });
    }
  }

  async function toggleUser(user: UserRecord) {
    try {
      await api.updateUser(user.id, { isActive: !user.is_active });
      await reloadUsers();
      notify({ tone: 'ok', text: user.is_active ? '사용자를 비활성화했습니다' : '사용자를 활성화했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '사용자 수정 실패' });
    }
  }

  async function addPermission() {
    const userId = Number(permissionUserId);
    if (!Number.isFinite(userId) || userId <= 0) {
      notify({ tone: 'error', text: '권한을 추가할 사용자를 선택해주세요' });
      return;
    }
    try {
      await api.addUserPermission(userId, { categoryKey: permissionCategory, platform: permissionPlatform });
      await reloadUsers();
      notify({ tone: 'ok', text: '권한을 추가했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '권한 추가 실패' });
    }
  }

  async function removePermission(user: UserRecord, permissionId?: number) {
    if (!permissionId) return;
    try {
      await api.deleteUserPermission(user.id, permissionId);
      await reloadUsers();
      notify({ tone: 'ok', text: '권한을 해제했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '권한 해제 실패' });
    }
  }

  return (
    <aside className="sp on">
      <div className="sp-hdr">
        <div className="sp-title">⚙ 설정</div>
        <button className="sp-close" onClick={onClose}>✕</button>
      </div>
      <div className="sp-body">
        <div className="sp-lbl">Local Debug Email</div>
        <div className="sp-row">
          <input className="sp-inp" value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="artubeditor@gmail.com" />
          <button
            className="btn-secondary btn-sm"
            onClick={() => {
              const next = draft.trim();
              if (next) localStorage.setItem('pick7reels.devEmail', next);
              else localStorage.removeItem('pick7reels.devEmail');
              setDevEmail(next);
            }}
          >
            적용
          </button>
        </div>
        <div className="sp-sep" />
        <div className="sp-lbl">API</div>
        <div className="sp-hint">{import.meta.env.VITE_PICK7REELS_API_BASE || 'https://pick7reels-worker.artubeditor.workers.dev'}</div>
        {role === 'admin' && (
          <>
            <div className="sp-sep" />
            <div className="sp-lbl">Apify API 토큰</div>
            <div className="sp-key-status">
              {keys.length ? keys.map((key) => (
                <div className="sp-key-row" key={key.id}>
                  <div className="sp-key-left">
                    <span className="sp-kdot on" />
                    <span className="sp-key-lbl">{key.label}</span>
                  </div>
                  <span className="sp-key-count active">{key.maskedKey} · {key.storage}</span>
                </div>
              )) : (
                <div className="sp-key-row">
                  <div className="sp-key-left">
                    <span className="sp-kdot" />
                    <span className="sp-key-lbl">등록된 토큰 없음</span>
                  </div>
                </div>
              )}
            </div>
            <input className="sp-inp" value={keyLabel} onChange={(event) => setKeyLabel(event.target.value)} placeholder="라벨" />
            <textarea
              className="sp-textarea"
              value={keyDraft}
              onChange={(event) => setKeyDraft(event.target.value)}
              placeholder="Apify API 토큰 원문은 저장 후 다시 표시하지 않습니다"
            />
            <button className="btn-primary" onClick={saveKey} disabled={!keyDraft.trim()}>토큰 저장</button>
            <div className="sp-sep" />
            <div className="sp-lbl">앱 URL</div>
            <input className="sp-inp" value={adminUrl} onChange={(event) => setAdminUrl(event.target.value)} placeholder="/admin" />
            <input className="sp-inp" value={editorUrl} onChange={(event) => setEditorUrl(event.target.value)} placeholder="/editor" />
            <label className="sp-check-row">
              <input type="checkbox" checked={geminiEnabled} onChange={(event) => setGeminiEnabled(event.target.checked)} />
              <span>Gemini 추천</span>
            </label>
            <button className="btn-secondary btn-sm" onClick={saveAppSettings}>설정 저장</button>
            <div className="sp-sep" />
            <div className="sp-lbl">편집자 권한</div>
            <div className="sp-row">
              <input
                className="sp-inp"
                value={newUserEmail}
                onChange={(event) => setNewUserEmail(event.target.value)}
                placeholder="editor@example.com"
              />
              <select className="sp-inp" value={newUserRole} onChange={(event) => setNewUserRole(event.target.value as Role)}>
                <option value="editor">editor</option>
                <option value="admin">admin</option>
              </select>
              <button className="btn-secondary btn-sm" onClick={createUser} disabled={!newUserEmail.trim()}>추가</button>
            </div>
            <div className="sp-row">
              <select className="sp-inp" value={permissionUserId} onChange={(event) => setPermissionUserId(event.target.value)}>
                {users.map((user) => <option key={user.id} value={user.id}>{user.email}</option>)}
              </select>
              <select className="sp-inp" value={permissionCategory} onChange={(event) => setPermissionCategory(event.target.value)}>
                {settingsCategories.map((category) => (
                  <option key={category.category_key} value={category.category_key}>{category.label}</option>
                ))}
              </select>
              <select className="sp-inp" value={permissionPlatform} onChange={(event) => setPermissionPlatform(event.target.value as Platform)}>
                <option value="instagram">Instagram</option>
                <option value="tiktok">TikTok</option>
              </select>
              <button className="btn-secondary btn-sm" onClick={addPermission} disabled={!permissionUserId}>권한 추가</button>
            </div>
            <div className="sp-key-status">
              {users.map((user) => (
                <div className="sp-user-row" key={user.id}>
                  <div className="sp-key-row">
                    <div className="sp-key-left">
                      <span className={`sp-kdot ${user.is_active ? 'on' : ''}`} />
                      <span className="sp-key-lbl">{user.email}</span>
                    </div>
                    <button className="mini-btn" onClick={() => toggleUser(user)}>
                      {user.is_active ? '활성' : '비활성'}
                    </button>
                  </div>
                  <div className="sp-permissions">
                    {(user.permissions || []).length ? (user.permissions || []).map((permission) => (
                      <button
                        className="mini-btn"
                        key={permission.id || `${permission.category_key}-${permission.platform}`}
                        onClick={() => removePermission(user, permission.id)}
                        title="클릭하면 권한 해제"
                      >
                        {permission.category_key} · {permission.platform} ✕
                      </button>
                    )) : <span className="sp-hint">권한 없음</span>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

function AuthPanel({
  error,
  devEmail,
  onLoggedIn,
  notify,
}: {
  error: string;
  devEmail: string;
  onLoggedIn: () => void;
  notify: (notice: Notice) => void;
}) {
  const [email, setEmail] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!email.trim() || !passphrase) {
      notify({ tone: 'error', text: '이메일과 접속 암호를 입력해주세요' });
      return;
    }
    setSubmitting(true);
    try {
      await api.login(email.trim(), passphrase);
      notify({ tone: 'ok', text: '로그인되었습니다' });
      onLoggedIn();
    } catch {
      notify({ tone: 'error', text: '이메일 또는 접속 암호가 올바르지 않습니다' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="content auth-panel">
      <div className="empty-box">
        <div className="empty-title">Pick7reels 로그인</div>
        <div className="empty-meta">{error}</div>
        <div className="auth-dev-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
          <input
            className="sp-inp"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') void submit(); }}
            placeholder="이메일"
          />
          <input
            className="sp-inp"
            type="password"
            value={passphrase}
            onChange={(event) => setPassphrase(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') void submit(); }}
            placeholder="접속 암호"
          />
          <button className="btn-primary" onClick={() => void submit()} disabled={submitting}>
            {submitting ? '로그인 중…' : '로그인'}
          </button>
        </div>
        {import.meta.env.DEV && (
          <div className="empty-meta" style={{ marginTop: '14px' }}>
            로컬 개발 환경에서는 우측 상단 ⚙ 설정의 Local Debug Email로 로그인 없이 특정 사용자로 접속할 수 있습니다.
            {devEmail ? ` (현재: ${devEmail})` : ''}
          </div>
        )}
      </div>
    </section>
  );
}

function SubTabs({
  tabs,
  active,
  onChange,
}: {
  tabs: [string, string][];
  active: string;
  onChange: (key: string) => void;
}) {
  return (
    <div className="sub-tabs">
      {tabs.map(([key, label]) => (
        <button key={key} className={`sub-tab ${active === key ? 'active' : ''}`} onClick={() => onChange(key)}>{label}</button>
      ))}
    </div>
  );
}

function ViewToggle({ value, onChange }: { value: 'card' | 'table'; onChange: (value: 'card' | 'table') => void }) {
  return (
    <div className="expl-view-btns" aria-label="보기 방식">
      <button className={`expl-view-btn ${value === 'card' ? 'active' : ''}`} onClick={() => onChange('card')}>▦</button>
      <button className={`expl-view-btn ${value === 'table' ? 'active' : ''}`} onClick={() => onChange('table')}>☰</button>
    </div>
  );
}

function Select({
  value,
  onChange,
  options,
  wide = false,
  disabled = false,
}: {
  value: string;
  onChange: (value: string) => void;
  options: string[][];
  wide?: boolean;
  disabled?: boolean;
}) {
  return (
    <select className={`rank-select ${wide ? 'wide' : 'slim'}`} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
      {options.map(([key, label]) => <option value={key} key={key}>{label}</option>)}
    </select>
  );
}

function NumberInput({ value, onChange, min, max }: { value: number; onChange: (value: number) => void; min: number; max: number }) {
  return (
    <label className="number-box">
      <span>결과</span>
      <input
        value={value}
        type="number"
        min={min}
        max={max}
        onChange={(event) => onChange(Math.max(min, Math.min(max, Number(event.target.value) || min)))}
      />
      <span>개</span>
    </label>
  );
}

function ConditionRow({
  condition,
  onPin,
  onDelete,
}: {
  condition: AssetCondition;
  onPin: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="condition-row">
      <div>
        <div className="condition-title">{assetTypeIcon(condition.condition_type)} {condition.label}</div>
        <div className="condition-meta">
          {condition.target === 'recommendation' ? '추천' : '예시'} · {condition.condition_type} · {condition.platform} · {condition.limit_count}개
        </div>
      </div>
      <div className="row-actions">
        <button className={`mini-btn ${condition.is_pinned ? 'purple active' : ''}`} onClick={onPin}>{condition.is_pinned ? '고정됨' : '고정'}</button>
        <button className="mini-btn danger" onClick={onDelete}>삭제</button>
      </div>
    </div>
  );
}

function DataState<T>({ state, emptyTitle }: { state: LoadState<T>; emptyTitle: string }) {
  const count = useMemo(() => {
    const data = state.data as Record<string, unknown> | null;
    if (!data) return 0;
    for (const key of ['items', 'posts', 'accounts', 'keywords', 'runs', 'conditions', 'categories', 'users']) {
      const value = data[key];
      if (Array.isArray(value)) return value.length;
    }
    return 0;
  }, [state.data]);

  if (state.status === 'loading') return <EmptyPanel title="불러오는 중" />;
  if (state.status === 'error') return <ErrorBox text={state.error} />;
  if (!count) return <EmptyPanel title={emptyTitle} />;
  return <EmptyPanel title={`${count}개 결과`} />;
}

function EmptyPanel({ title }: { title: string }) {
  return (
    <div className="empty-box">
      <div className="empty-title">{title}</div>
    </div>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div className="empty-box danger">
      <div className="empty-title">불러오기 실패</div>
      <div className="empty-meta">{text}</div>
    </div>
  );
}

function useApiData<T>(key: string, loader: () => Promise<T>): LoadState<T> {
  const [state, setState] = useState<LoadState<T>>({ status: 'loading', data: null, error: '' });
  useEffect(() => {
    let active = true;
    setState({ status: 'loading', data: null, error: '' });
    loader()
      .then((data) => {
        if (active) setState({ status: 'ready', data, error: '' });
      })
      .catch((error) => {
        if (active) setState({ status: 'error', data: null, error: error instanceof Error ? error.message : `${key} failed` });
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return state;
}

function groupConditions(conditions: AssetCondition[]) {
  return conditions.reduce<Record<string, AssetCondition[]>>((acc, condition) => {
    const key = condition.condition_type;
    acc[key] ||= [];
    acc[key].push(condition);
    return acc;
  }, {});
}

function assetTypeLabel(type: string) {
  if (type === 'keyword') return '키워드';
  if (type === 'hashtag') return '해시태그';
  if (type === 'account') return '계정';
  if (type === 'url') return 'URL';
  if (type === 'discover') return '연관 탐색';
  return type;
}

function assetTypeIcon(type: string) {
  if (type === 'keyword') return '🔎';
  if (type === 'hashtag') return '#️⃣';
  if (type === 'account') return '👤';
  if (type === 'url') return '🔗';
  if (type === 'discover') return '🧭';
  return '•';
}

function postId(item: PostResult) {
  return item.postId || item.post_id || item.shortcode || '';
}

function resultAccountId(item: AccountResult | PostResult) {
  return item.accountId || item.account_id || '';
}

function resultAccountName(item: AccountResult | PostResult) {
  return item.displayName || item.display_name || item.username || '계정';
}

function postUrl(item: PostResult) {
  const original = item.originalUrl || item.original_url || item.url;
  if (original) return original;
  if (item.platform === 'instagram') {
    const shortcode = item.shortcode || postId(item);
    return shortcode ? `https://www.instagram.com/reel/${shortcode}/` : '#';
  }
  if (item.platform === 'tiktok') {
    const id = postId(item);
    return item.username && id ? `https://www.tiktok.com/@${item.username}/video/${id}` : '#';
  }
  return '#';
}

function accountUrl(item: { platform?: Platform; username?: string; profileUrl?: string; profile_url?: string }) {
  const explicit = item.profileUrl || item.profile_url;
  if (explicit) return explicit;
  if (!item.username) return '#';
  return item.platform === 'tiktok' ? `https://www.tiktok.com/@${item.username}` : `https://www.instagram.com/${item.username}/`;
}

function accountTitle(item: PostResult | AccountResult) {
  if (item.username) return `@${item.username}`;
  return item.displayName || item.display_name || '계정';
}

function thumb(item: PostResult) {
  return item.thumbnail || '';
}

function viewCount(item: PostResult) {
  return item.viewCount ?? item.view_count ?? 0;
}

function formatViralScore(item: PostResult) {
  return formatMetric(item.viralScore ?? item.viral_score ?? item.score ?? item.final_score ?? 0);
}

function formatMetric(value: unknown) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return '-';
  return Math.round(num * 10) / 10;
}

function formatNumber(value: unknown) {
  const num = Number(value || 0);
  if (!Number.isFinite(num) || num <= 0) return '-';
  if (num >= 100000000) return `${Math.round(num / 10000000) / 10}억`;
  if (num >= 10000) return `${Math.round(num / 1000) / 10}만`;
  return new Intl.NumberFormat('ko-KR').format(Math.round(num));
}

function formatDate(value: string | null | undefined) {
  if (!value) return '-';
  return String(value).slice(0, 10).replaceAll('-', '.');
}

function formatUnixTime(value: unknown) {
  const seconds = Number(value || 0);
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  const date = new Date(seconds * 1000 + 9 * 3600000);
  return `${date.toISOString().slice(0, 10).replaceAll('-', '.')} ${date.toISOString().slice(11, 16)}`;
}

function hoursAgo(value: string | null | undefined) {
  if (!value) return '-';
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return '-';
  const diffMs = Date.now() - time;
  const hours = diffMs / 3600000;
  if (hours < 1) return '방금 전';
  if (hours < 24) return `${Math.floor(hours)}시간 전`;
  const days = hours / 24;
  if (days < 7) return `${Math.floor(days)}일 전`;
  return formatDate(value);
}

function captionPreview(item: PostResult, max = 90) {
  const caption = (item.caption || '').trim();
  if (!caption) return '캡션 없음';
  return caption.length > max ? `${caption.slice(0, max)}…` : caption;
}

function summarizeJobInput(run: ApifyRunInfo) {
  try {
    const input = JSON.parse(run.inputJson || run.input_json || '{}') as Record<string, unknown>;
    const parts = [input.query, input.hashtag, input.username, input.url].filter((value) => typeof value === 'string' && value);
    return parts.length ? parts.join(' · ') : '-';
  } catch {
    return '-';
  }
}

function archiveStatusLabel(value: string | null | undefined) {
  if (value === 'editing') return '편집 중';
  if (value === 'completed') return '편집 완료';
  if (value === 'revision_requested') return '수정 요청';
  if (value === 'revising') return '수정 중';
  return value || '-';
}

function archiveEventLabel(event: ArchiveEvent) {
  if (event.event_type === 'archived') return '확정됨';
  if (event.event_type === 'note_updated') return '메모 수정';
  if (event.event_type === 'status_changed') {
    return `${archiveStatusLabel(event.from_value)} → ${archiveStatusLabel(event.to_value)}`;
  }
  return event.note || event.event_type;
}

function pathForTab(role: Role, tabKey: string) {
  const routes = role === 'admin' ? adminRoutes : editorRoutes;
  return routes[tabKey] || (role === 'admin' ? '/admin' : '/editor');
}

function resolveTabForPath(pathname: string, role: Role, tabs: NavigationTab[]) {
  const allowed = new Set(tabs.map((tab) => tab.key));
  const routes = role === 'admin' ? adminRoutes : editorRoutes;
  const matched = Object.entries(routes).find(([, path]) => pathname === path || pathname.startsWith(`${path}/`));
  if (matched && allowed.has(matched[0])) return matched[0];
  return tabs[0]?.key || (role === 'admin' ? 'dashboard' : 'recommendation');
}

function replaceAppPath(path: string) {
  if (window.location.pathname !== path) {
    window.history.replaceState(null, '', path);
  }
}

function postArchivePayload(item: PostResult) {
  return {
    post_id: postId(item),
    platform: item.platform,
    caption: item.caption || '',
    account_id: resultAccountId(item),
    username: item.username || '',
    account_avatar: item.accountAvatar || item.account_avatar || '',
    thumbnail: thumb(item),
    duration_sec: item.durationSeconds || item.duration_sec || 0,
    view_count: viewCount(item),
    like_count: item.likeCount ?? item.like_count ?? 0,
    comment_count: item.commentCount ?? item.comment_count ?? 0,
    share_count: item.shareCount ?? item.share_count ?? 0,
    save_count: item.saveCount ?? item.save_count ?? 0,
    follower_count: item.followerCount ?? item.follower_count ?? 0,
    engagement_rate: item.engagementRate ?? item.engagement_rate ?? 0,
    viral_score: item.viralScore ?? item.viral_score ?? item.score ?? 0,
    original_url: item.originalUrl || item.original_url || item.url || '',
    uploaded_at: item.uploadedAt || item.uploaded_at || item.published_at || null,
  };
}

function todayKST() {
  return new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10);
}

export default App;
